import React, { useState, useEffect, useRef } from 'react';
import { AdmissionProfileFields } from '../panels/AdmissionProfileFields';
import { bearerHeaders } from '../api.ts';
import { getTheme, useThemeMode } from '../theme';
import { api, auth, QidUser, MeatOrder } from '../api';
import type {
  DogEntry, EquipmentEntry, WeaponLicenseEntry, CropState, WeaponEntry, LoanFormData, ProfileState,
} from '../types';
import {
  Loader,
  SignaturePad,
  fmtSEK,
} from '../components';
import { MyContracts } from '../ApplicationSystem';
import { LabelPrinter, LabelData as LabelDataType } from '../LabelPrinter';
import { apiFetch } from '../appFetch';
import { HunterApprovalStatus } from '../panels/HunterApprovalPanels';
import { DeleteAccountSection } from '../panels/DeleteAccountSection';
import { MyKillStats } from '../mark-tabs/StatistikTab'; // P6

const ALL_GAME_SPECIES = [
  'älg','dovhjort','kronhjort','rådjur','råbock','vildsvin','björn','lo',
  'varg','järv','räv','hare','fasan','orre','tjäder','järpe',
  'kanadagås','grågås','bläsand','and','ejder','beckasin',
];




const EQ_CATEGORIES = ['Tracking','Optik','Utrustning','Bearbetning','Transport','Övrigt'];
const EQ_CONDITIONS = ['Ny','Mycket bra skick','Bra skick','Acceptabelt skick'];

// ── MEAT ORDERS SUMMARY (profile view) ───────────────────────────
function MyMeatOrdersSummary(){
  const{mode}=useThemeMode();const T=getTheme(mode);
  const cS:React.CSSProperties={background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,padding:20};
  const[orders,setOrders]=useState<import('../api').MeatOrder[]>([]);
  const[loading,setLoading]=useState(true);
  const[confirming,setConfirming]=useState<string|null>(null);

  useEffect(()=>{
    api.myMeatOrders().then(setOrders).catch(()=>{}).finally(()=>setLoading(false));
  },[]);

  if(loading||orders.length===0)return null;

  const statusLabel:Record<string,string>={
    pending_payment:'Väntar betalning',paid:'Betald — väntar leverans',
    shipped:'Skickad',delivered:'Levererad',confirmed:'Bekräftad',
    disputed:'Tvist',refunded:'Återbetald',
  };
  const statusColor:Record<string,string>={
    pending_payment:T.amber,paid:'#00A2FF',shipped:'#00A2FF',
    delivered:'#61D836',confirmed:'#61D836',disputed:'#ff5555',refunded:T.textMuted,
  };

  async function confirm(orderId:string){
    setConfirming(orderId);
    try{
      await api.confirmMeatDelivery(orderId);
      setOrders(prev=>prev.map(o=>o.id===orderId?{...o,status:'confirmed'}:o));
    }catch(e:any){alert(e.message);}
    setConfirming(null);
  }

  return(
    <div style={{...cS,padding:'14px 16px',marginTop:12}}>
      <div style={{color:T.textSub,fontSize:12,marginBottom:12,fontWeight:600,textTransform:'uppercase' as const,letterSpacing:'0.05em'}}>Mina viltköttsköp</div>
      {orders.map(o=>(
        <div key={o.id} style={{borderBottom:`1px solid ${T.border}`,paddingBottom:12,marginBottom:12}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:6}}>
            <div>
              <div style={{color:T.text,fontSize:14,fontWeight:600}}>{o.species} {o.cut}</div>
              <div style={{color:T.textSub,fontSize:12}}>{o.sellerName} · {o.weightG>=1000?`${(o.weightG/1000).toFixed(1)} kg`:`${o.weightG} g`}</div>
              {o.escrow&&<div style={{color:T.textMuted,fontSize:11,marginTop:2}}>
                Escrow: <span style={{color:o.escrow.status==='released'?'#61D836':T.amber}}>{o.escrow.status==='held'?'Hålls av Qvenari':o.escrow.status==='released'?`${o.escrow.sellerAmount} kr frigörd`:o.escrow.status}</span>
              </div>}
            </div>
            <div style={{textAlign:'right' as const}}>
              <div style={{color:T.amber,fontSize:15,fontWeight:700}}>{o.totalPrice} kr</div>
              <span style={{background:statusColor[o.status]+'22',color:statusColor[o.status],fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:8}}>{statusLabel[o.status]||o.status}</span>
            </div>
          </div>
          {(o.status==='paid'||o.status==='shipped'||o.status==='delivered')&&(
            <button onClick={()=>confirm(o.id)} disabled={confirming===o.id} style={{marginTop:8,background:'#61D836',color:'#000',border:'none',borderRadius:4,padding:'7px 16px',fontWeight:700,fontSize:12,cursor:'pointer'}}>
              {confirming===o.id?'Bekräftar...':'Bekräfta mottagande — frigör till säljaren'}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function VerifyIdentityPanel({ user, token, onVerified, T }: { user: any, token: string, onVerified: (level: number) => void, T: any }) {
  const level = (user?.verification_level ?? 0) as number;
  const [status, setStatus] = React.useState<any>(null);
  const [step, setStep] = React.useState<'idle'|'photo'|'mrz_review'|'nfc'|'done'>('idle');
  const [capturedImage, setCapturedImage] = React.useState<string>('');
  const [mrzData, setMrzData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const streamRef = React.useRef<MediaStream|null>(null);

  React.useEffect(() => {
    if (!token) return;
    fetch('/auth/verify/status', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setStatus).catch(() => {});
  }, [token]);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: 1280, height: 720 } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setStep('photo');
    } catch {
      setMsg('Kunde inte öppna kameran. Kontrollera behörigheter.');
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d', { colorSpace: 'srgb' });
    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;
    ctx?.drawImage(videoRef.current, 0, 0);
    const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.92);
    setCapturedImage(dataUrl);
    stopCamera();
    setStep('mrz_review');
    extractMRZ(dataUrl);
  };

  const extractMRZ = async (dataUrl: string) => {
    setLoading(true);
    setMsg('Analyserar passet...');
    try {
      const base64 = dataUrl.split(',')[1];
      const r = await fetch('/auth/verify/extract-mrz', {
        method: 'POST',
        headers: { ...bearerHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: base64 })
      });
      const data = await r.json();
      if (data.mrz) {
        setMrzData(data.mrz);
        setMsg('MRZ-data extraherad — granska och bekräfta.');
      } else {
        setMsg('Kunde inte läsa MRZ. Försök igen med bättre belysning.');
        setStep('idle');
      }
    } catch {
      setMsg('Fel vid analys. Försök igen.');
      setStep('idle');
    } finally {
      setLoading(false);
    }
  };

  const submitMRZ = async () => {
    if (!mrzData) return;
    setLoading(true);
    setMsg('Verifierar dokument...');
    try {
      const r = await fetch('/auth/verify/level2', {
        method: 'POST',
        headers: { ...bearerHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ mrz: mrzData })
      });
      const data = await r.json();
      if (r.ok) {
        setMsg('✓ Identitet verifierad på nivå 2!');
        onVerified(2);
        setStep('done');
        fetch('/auth/verify/status', { headers: { Authorization: `Bearer ${token}` } })
          .then(r2 => r2.json()).then(setStatus).catch(() => {});
      } else {
        setMsg(data.error ?? 'Verifiering misslyckades. Kontrollera att dokumentet är giltigt.');
        setStep('idle');
      }
    } catch {
      setMsg('Nätverksfel. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  // iOS-appen (CoreNFC-pluginet). Android-appen har inget NFC-plugin än och går webbvägen.
  const isNativeApp = () => !!(window as any).Capacitor?.isNativePlatform?.() && (window as any).Capacitor?.getPlatform?.() === 'ios';

  const tryNFC = async () => {
    setStep('nfc');
    setMsg('Förbereder NFC...');

    if (isNativeApp()) {
      // ─── iOS Native: use CoreNFC via Capacitor plugin ───────────────
      await tryNFC_iOS();
    } else if ('NDEFReader' in window) {
      // ─── Android Chrome: use Web NFC API (chip presence only) ───────
      await tryNFC_Android();
    } else {
      setMsg('NFC stöds inte i den här webbläsaren. Använd venari-appen på iPhone eller Chrome på Android.');
      setStep('idle');
    }
  };

  // iOS CoreNFC (BAC passport reading)
  const tryNFC_iOS = async () => {
    if (!mrzData) {
      setMsg('Du måste fotografera passet (steg 1) innan NFC-läsning.');
      setStep('idle');
      return;
    }
    setMsg('Håll passets baksida mot iPhone...');
    try {
      const { NFCPassport } = (window as any).Capacitor.Plugins;
      if (!NFCPassport) throw new Error('NFC-plugin saknas');

      const result = await NFCPassport.readPassport({
        docNumber: mrzData.document_number ?? '',
        dateOfBirth: mrzData.date_of_birth ?? '',
        expiryDate: mrzData.expiry_date ?? ''
      });

      if (result?.success) {
        setLoading(true);
        setMsg('Passdata läst — sparar verifiering...');
        const r = await fetch('/auth/verify/level3', {
          method: 'POST',
          headers: { ...bearerHeaders(token), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chip: {
              nfc_verified: true,
              mrz_confirmed: result.mrzConfirmed ?? false,
              mrz_raw: result.mrzRaw ?? '',
              platform: 'ios_corenfc'
            }
          })
        });
        const data = await r.json();
        if (r.ok) {
          setMsg('✓ Nivå 3 NFC-verifiering klar!');
          onVerified(3);
          setStep('done');
        } else {
          setMsg(data.error ?? 'Verifiering misslyckades.');
          setStep('idle');
        }
        setLoading(false);
      } else {
        setMsg(result?.message ?? 'Passläsning misslyckades. Försök igen.');
        setStep('idle');
      }
    } catch (e: any) {
      const errMsg = e?.message ?? 'NFC-fel';
      if (errMsg.includes('User cancelled')) {
        setMsg('NFC-läsning avbröts.');
      } else {
        setMsg(`NFC-fel: ${errMsg}`);
      }
      setStep('idle');
    }
  };

  // Android Chrome: Web NFC (NDEFReader — detects chip presence)
  const tryNFC_Android = async () => {
    setMsg('Håll passets bakre informationssida mot telefonens baksida...');
    try {
      const ndef = new (window as any).NDEFReader();
      await (ndef as any).scan();

      let chipDetected = false;
      (ndef as any).addEventListener('reading', async ({ serialNumber }: any) => {
        if (chipDetected) return;
        chipDetected = true;
        setMsg('Passchip detekterat — verifierar...');
        setLoading(true);
        try {
          const r = await fetch('/auth/verify/level3', {
            method: 'POST',
            headers: { ...bearerHeaders(token), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              chip: {
                nfc_verified: true,
                chip_uid: serialNumber ?? '',
                mrz_confirmed: false,
                platform: 'android_webnfc'
              }
            })
          });
          const data = await r.json();
          if (r.ok) {
            setMsg('✓ Nivå 3 NFC-verifiering klar!');
            onVerified(3);
            setStep('done');
          } else {
            setMsg(data.error ?? 'Verifiering misslyckades.');
            setStep('idle');
          }
        } finally {
          setLoading(false);
        }
      });

      // Timeout after 30s
      setTimeout(() => {
        if (!chipDetected) {
          setMsg('Timeout — inget passchip hittades. Kontrollera att passet hålls tätt mot telefonen.');
          setStep('idle');
        }
      }, 30000);
    } catch (e: any) {
      setMsg(e?.message ?? 'NFC-läsning misslyckades.');
      setStep('idle');
    }
  };

  const resetFlow = () => {
    stopCamera();
    setStep('idle');
    setMsg('');
    setCapturedImage('');
    setMrzData(null);
  };

  const levelLabel = (l: number) => ['Ej verifierad','E-post verifierad','ID-dokument','ePass NFC','Biometrisk'][l] ?? `Nivå ${l}`;
  const levelColor = (l: number) => ['#9ca3af','#3b82f6','#f59e0b','#22c55e','#8b5cf6'][l] ?? T.text;

  return (
    <div>
      {/* Level indicator */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[0,1,2,3].map(l => (
          <div key={l} style={{
            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            background: level >= l ? levelColor(l) : T.border,
            color: level >= l ? '#fff' : T.textMuted ?? '#888'
          }}>
            {l === 0 ? 'Konto' : l === 1 ? 'E-post' : l === 2 ? 'ID-dok' : 'NFC Pass'}
            {level >= l && ' ✓'}
          </div>
        ))}
      </div>

      {level < 2 && step === 'idle' && (
        <div>
          <p style={{ fontSize: 13, color: T.textMuted ?? '#888', marginBottom: 12 }}>
            Verifiera din identitet med ett giltigt pass eller nationellt ID-kort. Informationen hanteras av Qid och delas inte med tredje part.
          </p>
          <button onClick={startCamera} style={{
            background: T.amber, color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer'
          }}>
            📷 Fotografera pass/ID-kort
          </button>
        </div>
      )}

      {step === 'photo' && (
        <div>
          <p style={{ fontSize: 13, color: T.text, marginBottom: 8 }}>Rikta kameran mot MRZ-zonen (raderna längst ner på dokumentet)</p>
          <video ref={videoRef} style={{ width: '100%', borderRadius: 8, maxHeight: 280, objectFit: 'cover' }} playsInline muted />
          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <button onClick={capturePhoto} style={{
              background: '#22c55e', color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer', flex: 1
            }}>📸 Ta foto</button>
            <button onClick={resetFlow} style={{
              background: T.border, color: T.text, border: 'none', borderRadius: 8,
              padding: '10px 16px', fontSize: 14, cursor: 'pointer'
            }}>Avbryt</button>
          </div>
        </div>
      )}

      {step === 'mrz_review' && (
        <div>
          {capturedImage && <img src={capturedImage} style={{ width: '100%', borderRadius: 8, maxHeight: 200, objectFit: 'cover', marginBottom: 12 }} />}
          {loading && <div style={{ color: T.amber, fontSize: 14, marginBottom: 8 }}>{msg || 'Arbetar...'}</div>}
          {mrzData && !loading && (
            <div style={{ background: T.bg, borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                <span style={{ color: T.textMuted ?? '#888' }}>Efternamn:</span><span style={{ color: T.text, fontWeight: 600 }}>{mrzData.surname}</span>
                <span style={{ color: T.textMuted ?? '#888' }}>Förnamn:</span><span style={{ color: T.text, fontWeight: 600 }}>{mrzData.given_names}</span>
                <span style={{ color: T.textMuted ?? '#888' }}>Nationalitet:</span><span style={{ color: T.text }}>{mrzData.nationality}</span>
                <span style={{ color: T.textMuted ?? '#888' }}>Födelsedag:</span><span style={{ color: T.text }}>{mrzData.date_of_birth}</span>
                <span style={{ color: T.textMuted ?? '#888' }}>Giltigt t.o.m.:</span><span style={{ color: T.text }}>{mrzData.expiry_date}</span>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                <button onClick={submitMRZ} style={{
                  background: '#22c55e', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer'
                }}>✓ Bekräfta och verifiera</button>
                <button onClick={resetFlow} style={{
                  background: T.border, color: T.text, border: 'none', borderRadius: 8,
                  padding: '10px 14px', fontSize: 13, cursor: 'pointer'
                }}>Försök igen</button>
              </div>
            </div>
          )}
          {!loading && !mrzData && <button onClick={resetFlow} style={{
            background: T.amber, color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 20px', fontSize: 14, cursor: 'pointer'
          }}>Försök igen</button>}
        </div>
      )}

      {step === 'done' && (
        <div>
          <div style={{ color: '#22c55e', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>✓ Identiteten verifierad!</div>
          {level >= 2 && level < 3 && (
            <button onClick={tryNFC} style={{
              background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 20px', fontSize: 14, cursor: 'pointer', marginTop: 8
            }}>
              📡 Uppgradera till nivå 3 (NFC-chip)
            </button>
          )}
        </div>
      )}

      {step === 'nfc' && (
        <div style={{ textAlign: 'center', padding: '20px 12px' }}>
          {/* Passport + phone illustration */}
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: 16 }}>
            {/* Passport */}
            <div style={{
              width: 90, height: 126, borderRadius: 8, background: '#1a3a5c',
              display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              marginRight: 12, verticalAlign: 'middle'
            }}>
              <div style={{ color: '#c8a73a', fontSize: 22, marginBottom: 4 }}>◎</div>
              <div style={{ color: '#c8a73a', fontSize: 9, letterSpacing: 1 }}>PASSPORT</div>
              <div style={{ color: 'rgba(200,167,58,0.5)', fontSize: 7, marginTop: 8, letterSpacing: 0.5 }}>
                SVERIGE<br/>SWEDEN
              </div>
              {/* NFC symbol */}
              <div style={{ position: 'absolute', bottom: 8, right: 8, color: '#22c55e', fontSize: 16 }}>
                {loading ? '⟳' : '📡'}
              </div>
            </div>
            {/* Phone */}
            <div style={{
              width: 50, height: 90, borderRadius: 10, background: '#222',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              border: '2px solid #444', verticalAlign: 'middle', position: 'relative'
            }}>
              <div style={{
                width: 36, height: 70, borderRadius: 6, background: '#1a1a1a',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18
              }}>
                {loading
                  ? <span style={{ color: '#22c55e', animation: 'pulse 1s infinite' }}>📡</span>
                  : <span style={{ color: '#3b82f6' }}>📱</span>}
              </div>
            </div>
          </div>

          {/* Instructions */}
          {!loading ? (
            <div style={{ marginBottom: 12 }}>
              <p style={{ color: T.text, fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                {isNativeApp() ? 'Läser passchiPet via CoreNFC' : 'Detekterar NFC-chip'}
              </p>
              <ol style={{ textAlign: 'left', color: T.textMuted ?? '#888', fontSize: 12, paddingLeft: 20, margin: 0 }}>
                <li style={{ marginBottom: 4 }}>Öppna passet till bildsidan</li>
                <li style={{ marginBottom: 4 }}>Håll passets baksida (informationssidan med chipikon) mot baksidan av telefonen</li>
                <li style={{ marginBottom: 4 }}>Håll stilla i 3–5 sekunder tills du känner en vibration</li>
              </ol>
              {!isNativeApp() && (
                <p style={{ color: T.amber, fontSize: 11, marginTop: 8, fontStyle: 'italic' }}>
                  Bäst resultat med venari-appen på iPhone (fullständig BAC-läsning)
                </p>
              )}
            </div>
          ) : (
            <div style={{ color: '#22c55e', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
              {msg}
            </div>
          )}

          {!loading && (
            <div style={{ color: T.textMuted ?? '#888', fontSize: 12, marginBottom: 16 }}>
              {msg}
            </div>
          )}

          <button onClick={resetFlow} style={{
            background: T.border, color: T.text, border: 'none',
            borderRadius: 8, padding: '8px 20px', cursor: 'pointer', fontSize: 13
          }}>Avbryt</button>
        </div>
      )}

      {step === 'idle' && level >= 2 && (
        <div>
          <div style={{ color: '#22c55e', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
            ✓ Verifierad - {levelLabel(level)}
          </div>
          {level === 2 && (
            <div>
              <button onClick={tryNFC} style={{
                background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8,
                padding: '10px 18px', fontSize: 13, cursor: 'pointer', fontWeight: 700
              }}>
                📡 Uppgradera till nivå 3 (NFC-chip)
              </button>
              <p style={{ fontSize: 11, color: T.textMuted ?? '#888', marginTop: 6, marginBottom: 0 }}>
                {isNativeApp()
                  ? 'Kräver pass med NFC-chip (biometriskt pass, utgävet efter 2006 i Sverige)'
                  : 'Stöds i venari-appen (iPhone) och Chrome på Android'}
              </p>
            </div>
          )}
        </div>
      )}

      {msg && step !== 'mrz_review' && step !== 'nfc' && (
        <div style={{ marginTop: 10, color: msg.startsWith('✓') ? '#22c55e' : '#ef4444', fontSize: 13 }}>{msg}</div>
      )}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
}


function randomId(){return Math.random().toString(36).slice(2,10);}

export function VProfil({onAvatarUpdate,user,onLogout,onOpenSettings}:{onAvatarUpdate?:(url:string|null)=>void;user?:QidUser|null;onLogout?:()=>void;/** Till Inställningar (notiser, integrationer, utseende). */onOpenSettings?:()=>void}={onAvatarUpdate:undefined,user:undefined,onLogout:undefined}){
  const[prof,setProf]=useState<ProfileState>({
    bio:'',experience_level:'medel',hunting_region:'',avatar_url:'',
    license_urls:[],game_kills:{},dogs:[],preferred_game:[],preferred_weapons:[],
    certifications:'',meat_need_kg_per_year:'',budget_per_season:'',
    equipment:[],
  });
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(false);
  const[saved,setSaved]=useState(false);
  const[err,setErr]=useState<string|null>(null);
  const[uploadingAvatar,setUploadingAvatar]=useState(false);
  const[uploadingLicense,setUploadingLicense]=useState(false);
  const avatarRef=useRef<HTMLInputElement>(null);
  const cameraRef=useRef<HTMLInputElement>(null);
  const licenseRef=useRef<HTMLInputElement>(null);

  // ── INTEGRATIONS STATE ──────────────────────────────────────
  // Integrationerna finns under Inställningar (VInstallningar); här behövs bara Garmin-enheterna för hundarna.
  const[garminDevicesList,setGarminDevicesList]=useState<any[]>([]);

  const token=localStorage.getItem('qvenari_access_token');
  const authH={...bearerHeaders(token||''),'Content-Type':'application/json'};

  useEffect(()=>{
    if(!token){setLoading(false);return;}
    apiFetch('/api/profile',{headers:{...bearerHeaders(token)}})
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if(d){
          setProf(p=>({
            ...p,
            bio:d.bio||'',experience_level:d.experience_level||'medel',
            hunting_region:d.hunting_region||'',avatar_url:d.avatar_url||'',
            license_urls:d.license_urls||[],game_kills:d.game_kills||{},
            dogs:d.dogs||[],preferred_game:d.preferred_game||[],
            preferred_weapons:d.preferred_weapons||[],
            certifications:(d.certifications||[]).join('\n'),
            meat_need_kg_per_year:d.meat_need_kg_per_year?String(d.meat_need_kg_per_year):'',
            budget_per_season:d.budget_per_season?String(d.budget_per_season):'',
          }));
          if(d.hunter_exam) setHunterExam(d.hunter_exam);
        }
        setLoading(false);
      })
      .catch(()=>setLoading(false));
    // Load equipment from GraphQL
    api.myEquipment().then(items=>{
      setProf(p=>({...p, equipment: items.map(eq=>({
        id:eq.id, name:eq.name, category:eq.category, description:eq.description||'',
        image_url:eq.imageUrl||'', condition:eq.condition||'Bra skick',
        for_rent:eq.forRent, rent_price:eq.rentPrice?String(eq.rentPrice):'',
        for_sale:eq.forSale, sale_price:eq.salePrice?String(eq.salePrice):'',
        qregistry_object_id:eq.qregistryObjectId||'',
      }))}));
    }).catch(()=>{});
    // Load weapons from GraphQL
    api.myWeapons().then(weps=>{
      setWeapons(weps.map((w:any)=>({
        id:w.id, name:w.name, weaponType:w.weaponType||'Gevär',
        manufacturer:w.manufacturer||'', model:w.model||'',
        serialNumber:w.serialNumber||'', caliber:w.caliber||'',
        licenseNumber:w.licenseNumber||'', licenseType:w.licenseType||'Jakt',
        licenseHolder:w.licenseHolder||'', licenseExpires:w.licenseExpires||'',
        licenseImageUrl:w.licenseImageUrl||'', licenseImageUrlBack:w.licenseImageUrlBack||'', description:w.description||'',
        image_url:w.imageUrl||'', condition:w.condition||'Bra skick',
        for_rent:w.forRent, rent_price:String(w.rentPrice||0),
        for_sale:w.forSale, sale_price:String(w.salePrice||0),
        licenses:(w.licenses||[]).map((l:any)=>({
          id:l.id||'',partType:l.partType||'Helgevär',serialNumber:l.serialNumber||'',
          licenseNumber:l.licenseNumber||'',licenseType:l.licenseType||'Jakt',
          licenseHolder:l.licenseHolder||'',licenseExpires:l.licenseExpires||'',
          licenseImageUrl:l.licenseImageUrl||'',licenseImageUrlBack:l.licenseImageUrlBack||'',
          caliber:l.caliber||'',notes:l.notes||'',
        })),
      })));
    }).catch(()=>{});
  },[]);

  useEffect(()=>{
    if(!token)return;
    const H={...bearerHeaders(token),'X-Api-Key':'dev-secret'};
    apiFetch('/api/profile/integrations/garmin/devices',{headers:H})
      .then(r=>r.ok?r.json():[])
      .then(setGarminDevicesList)
      .catch(()=>{});
  },[token]);

  const save=async()=>{
    setSaving(true);setErr(null);
    try{
      const body={
        bio:prof.bio,experience_level:prof.experience_level,hunting_region:prof.hunting_region,
        avatar_url:prof.avatar_url,license_urls:prof.license_urls,game_kills:prof.game_kills,
        dogs:prof.dogs,preferred_game:prof.preferred_game,
        preferred_weapons:prof.preferred_weapons.filter(Boolean),
        certifications:prof.certifications.split('\n').filter(Boolean),
        meat_need_kg_per_year:prof.meat_need_kg_per_year?Number(prof.meat_need_kg_per_year):0,
        budget_per_season:prof.budget_per_season?Number(prof.budget_per_season):0,
        hunter_exam:hunterExam,
      };
      const r=await apiFetch('/api/profile',{method:'PUT',headers:authH,body:JSON.stringify(body)});
      if(!r.ok)throw new Error('Kunde inte spara');
      setSaved(true);setTimeout(()=>setSaved(false),3000);
    }catch(e:unknown){setErr(e instanceof Error?e.message:'Fel vid sparning');}
    setSaving(false);
  };

  const uploadFile=async(file:File,subdir:'images'|'docs'):Promise<string>=>{
    const fd=new FormData();fd.append('file',file);
    const r=await apiFetch('/api/upload',{method:'POST',body:fd});
    if(!r.ok)throw new Error('Upload failed');
    const d=await r.json();
    return d.url as string;
  };

  const onAvatarChange=async(e:React.ChangeEvent<HTMLInputElement>)=>{
    const f=e.target.files?.[0];if(!f)return;
    setUploadingAvatar(true);
    try{
      const url=await uploadFile(f,'images');
      setProf(p=>({...p,avatar_url:url}));
      if(onAvatarUpdate)onAvatarUpdate(url);
      // Auto-save avatar_url immediately to DB
      await apiFetch('/api/profile',{method:'PUT',headers:authH,body:JSON.stringify({avatar_url:url})});
    }
    catch(ex){setErr('Kunde inte ladda upp profilbild');}
    setUploadingAvatar(false);
    e.target.value='';
  };

  const onLicenseChange=async(e:React.ChangeEvent<HTMLInputElement>)=>{
    const files=Array.from(e.target.files||[]);if(!files.length)return;
    setUploadingLicense(true);
    try{
      const urls=await Promise.all(files.map(f=>uploadFile(f,'images')));
      setProf(p=>({...p,license_urls:[...p.license_urls,...urls]}));
    }catch(ex){setErr('Kunde inte ladda upp licens');}
    setUploadingLicense(false);
    e.target.value='';
  };

  const rmLicense=(url:string)=>setProf(p=>({...p,license_urls:p.license_urls.filter(u=>u!==url)}));
  const setKill=(species:string,v:number)=>setProf(p=>({...p,game_kills:{...p.game_kills,[species]:Math.max(0,v)}}));
  const addDog=()=>setProf(p=>({...p,dogs:[...p.dogs,{id:randomId(),name:'',breed:'',age:'',trail_km:'',notes:'',image_url:''}]}));
  const rmDog=(id:string)=>setProf(p=>({...p,dogs:p.dogs.filter(d=>d.id!==id)}));
  const updDog=(id:string,field:keyof DogEntry,val:string)=>setProf(p=>({...p,dogs:p.dogs.map(d=>d.id===id?{...d,[field]:val}:d)}));
  const dogImgRef=useRef<Record<string,HTMLInputElement|null>>({});
  const[uploadingDogImg,setUploadingDogImg]=useState<Record<string,boolean>>({});
  const handleDogImg=async(dogId:string,e:React.ChangeEvent<HTMLInputElement>)=>{
    const file=e.target.files?.[0];if(!file)return;
    setUploadingDogImg(p=>({...p,[dogId]:true}));
    try{const res=await uploadFile(file,'images');updDog(dogId,'image_url',res);}
    catch{setErr('Kunde inte ladda upp hundbild');}
    setUploadingDogImg(p=>({...p,[dogId]:false}));
    e.target.value='';
  };
  const togGame=(g:string)=>setProf(p=>({...p,preferred_game:p.preferred_game.includes(g)?p.preferred_game.filter(x=>x!==g):[...p.preferred_game,g]}));

  // ── EQUIPMENT CRUD ──────────────────────────────────────────
  const[eqSaving,setEqSaving]=useState<Record<string,boolean>>({});
  const eqImgRef=useRef<Record<string,HTMLInputElement|null>>({});

  // ── WEAPON STATE ─────────────────────────────────────────────
  const[weapons,setWeapons]=useState<WeaponEntry[]>([]);
  const[weaponGardTab,setWeaponGardTab]=useState<'utrustning'|'vapen'>('utrustning');
  const[editingWeapon,setEditingWeapon]=useState<WeaponEntry|null>(null);
  const[showWeaponForm,setShowWeaponForm]=useState(false);
  const[ocrLoading,setOcrLoading]=useState(false);
  const[cropState,setCropState]=useState<CropState|null>(null);
  const[editingLicense,setEditingLicense]=useState<WeaponLicenseEntry|null>(null);
  const[editingLicenseIdx,setEditingLicenseIdx]=useState<number|null>(null);
  const[weaponSavedId,setWeaponSavedId]=useState<string|null>(null);
  const[loanForm,setLoanForm]=useState<LoanFormData|null>(null);
  const[loanLoading,setLoanLoading]=useState(false);
  const[loanFormWeapon,setLoanFormWeapon]=useState<WeaponEntry|null>(null);

  // ── JÄGAREXAMEN STATE ─────────────────────────────────────────
  const[hunterExam,setHunterExam]=useState<{imageUrl:string;holderName:string;examDate:string;examNumber:string;issuedBy:string;ocrRaw:string}|null>(null);
  const[hunterExamLoading,setHunterExamLoading]=useState(false);

  // ── EDIT MODE ────────────────────────────────────────────────
  const[editMode,setEditMode]=useState(false);
  const{mode:profThemeMode}=useThemeMode();
  const T = getTheme(profThemeMode);
  const iS:React.CSSProperties={width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:4,padding:'10px 14px',color:T.text,fontSize:14,outline:'none',boxSizing:'border-box',fontFamily:'inherit'};
  const lS:React.CSSProperties={display:'block',color:T.textSub,fontSize:11,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:6};
  const bP:React.CSSProperties={background:T.amber,color:'#000',border:'none',borderRadius:5,padding:'12px 24px',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'inherit'};
  const bSc:React.CSSProperties={background:T.surface,color:T.text,border:`1px solid ${T.border}`,borderRadius:5,padding:'12px 24px',fontSize:14,cursor:'pointer',fontFamily:'inherit'};
  const cS:React.CSSProperties={background:T.surface,border:`1px solid ${T.border}`,borderRadius:6,padding:20};

  // ── EQUIPMENT LABEL PRINTING ─────────────────────────────────
  const[showEquipLabels,setShowEquipLabels]=useState(false);
  const[equipLabelData,setEquipLabelData]=useState<LabelDataType[]>([]);

  const openEquipLabels=()=>{
    // Build one label per registered equipment item + weapon
    const allItems:[string,string,string][]=[
      ...prof.equipment.map(eq=>[eq.name||'Utrustning',eq.category||'Övrigt',eq.id] as [string,string,string]),
      ...weapons.filter(w=>w.id).map(w=>[w.name||'Vapen',w.weaponType||'Gevär',w.id] as [string,string,string]),
    ];
    if(allItems.length===0){alert('Inga utrustning eller vapen registrerade ännu.');return;}
    const labels:LabelDataType[]=allItems.map(([name,cat,id])=>{
      const labelId=id.length>10?id:('eq-'+Math.random().toString(36).slice(2,10));
      return{
        id:labelId,
        species:cat,
        cut:name,
        markName:'',
        skjutetDate:'',styckatDate:'',
        weightKg:'',kgPrice:'',autoPrice:false,totalPrice:'',
        qrValue:`https://qvenari.se/v/${labelId}`,
        notes:'',
        imgX:50,imgY:50,imgScale:1,
      };
    });
    setEquipLabelData(labels);
    setShowEquipLabels(true);
  };

  const addEquipment=async()=>{
    const tempId=randomId();
    const newEq:EquipmentEntry={id:tempId,name:'',category:'Övrigt',description:'',image_url:'',condition:'Bra skick',for_rent:false,rent_price:'',for_sale:false,sale_price:'',qregistry_object_id:''};
    setProf(p=>({...p,equipment:[...p.equipment,newEq]}));
  };

  const saveEquipment=async(eq:EquipmentEntry)=>{
    if(!eq.name.trim())return;
    setEqSaving(s=>({...s,[eq.id]:true}));
    try{
      const input={
        name:eq.name, category:eq.category, description:eq.description||undefined,
        imageUrl:eq.image_url||undefined, condition:eq.condition,
        forRent:eq.for_rent, rentPrice:eq.rent_price?Number(eq.rent_price):0,
        forSale:eq.for_sale, salePrice:eq.sale_price?Number(eq.sale_price):0,
      };
      // If the id is short (local temp id), create new; otherwise update existing
      if(eq.id.length<20){
        const created=await api.createEquipment(input);
        setProf(p=>({...p,equipment:p.equipment.map(e=>e.id===eq.id?{...e,id:created.id,qregistry_object_id:''}:e)}));
      }else{
        await api.updateEquipment(eq.id,input);
      }
    }catch(e){setErr(e instanceof Error?e.message:'Kunde inte spara utrustning');}
    setEqSaving(s=>({...s,[eq.id]:false}));
  };

  const rmEquipment=async(id:string)=>{
    if(id.length>=20){try{await api.deleteEquipment(id);}catch{}}
    setProf(p=>({...p,equipment:p.equipment.filter(e=>e.id!==id)}));
  };

  const updEquipment=(id:string,field:keyof EquipmentEntry,val:string|boolean)=>{
    setProf(p=>({...p,equipment:p.equipment.map(e=>e.id===id?{...e,[field]:val}:e)}));
  };

  const onEqImageChange=async(eqId:string,e:React.ChangeEvent<HTMLInputElement>)=>{
    const f=e.target.files?.[0];if(!f)return;
    try{
      const url=await uploadFile(f,'images');
      updEquipment(eqId,'image_url',url);
    }catch{setErr('Kunde inte ladda upp bild');}
    e.target.value='';
  };

  if(loading)return<Loader text="Hämtar profil..."/>;
  if(!token)return<div style={{padding:48,textAlign:'center'}}><div style={{color:T.text,fontSize:18,fontWeight:600,marginBottom:8}}>Logga in för att se din profil</div><div style={{color:T.textSub,fontSize:14}}>Klicka på Logga in-knappen uppe i höger hörn</div></div>;

  const initials=(prof.avatar_url?'':undefined);

  // ── COMPACT SUMMARY VIEW ─────────────────────────────────────
  if(!editMode){
    const totalKills=Object.values(prof.game_kills).reduce((a,b)=>a+b,0);
    const expLabel:Record<string,string>={nyborjare:'Nybörjare',medel:'Medel',erfaren:'Erfaren',expert:'Expert'};
    return(
      <div className="view-pad" style={{paddingTop:160,paddingBottom:24,paddingLeft:'clamp(12px,3vw,28px)',paddingRight:'clamp(12px,3vw,28px)',maxWidth:760,margin:'0 auto'}}>
        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,position:'relative',zIndex:20}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <h1 style={{color:T.text,fontSize:20,fontWeight:700,margin:0}}>Min profil</h1>
            {(auth.getUser() as any)?.verification_level >= 2 && (
              <span style={{background:'#22c55e',color:'#fff',borderRadius:20,padding:'2px 10px',fontSize:12,fontWeight:700,flexShrink:0}}>✓ Verifierad</span>
            )}
          </div>
          <button onClick={()=>setEditMode(true)} style={{background:T.amber,color:'#000',border:'none',borderRadius:4,padding:'8px 20px',fontWeight:700,fontSize:14,cursor:'pointer',flexShrink:0}}>Redigera</button>
        </div>

        {user&&onLogout&&(
          <div style={{...cS,marginBottom:16,display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:14,flexWrap:'wrap'}}>
            <div style={{minWidth:0,flex:1}}>
              <div style={{color:T.textSub,fontSize:11,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:6}}>Konto</div>
              <div style={{color:T.text,fontSize:15,fontWeight:600,wordBreak:'break-word',lineHeight:1.35}}>{user.name||user.email}</div>
              {user.email&&(user.name||'')!==user.email&&<div style={{color:T.textMuted,fontSize:13,marginTop:4,wordBreak:'break-all'}}>{user.email}</div>}
            </div>
            <button type="button" onClick={onLogout} style={{...bSc,padding:'10px 18px',fontSize:13,fontWeight:600,flexShrink:0,alignSelf:'center'}}>Logga ut</button>
          </div>
        )}

        {user&&<DeleteAccountSection cardStyle={cS} onDeleted={onLogout}/>}

        {onOpenSettings&&(
          <button type="button" onClick={onOpenSettings} style={{...cS,width:'100%',marginBottom:16,display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,cursor:'pointer',fontFamily:'inherit',textAlign:'left',color:T.text}}>
            <span>
              <span style={{display:'block',fontSize:14,fontWeight:600}}>Inställningar</span>
              <span style={{display:'block',color:T.textSub,fontSize:12,marginTop:2}}>Utseende, notiser, integrationer och support</span>
            </span>
            <span aria-hidden style={{color:T.textSub,fontSize:18}}>›</span>
          </button>
        )}

        {/* Avatar + basics */}
        <div style={{...cS,marginBottom:16,display:'flex',gap:20,alignItems:'center',flexWrap:'wrap',position:'relative',zIndex:1}}>
          <div style={{width:80,height:80,borderRadius:'50%',overflow:'hidden',background:T.surfaceHover,border:`3px solid ${T.amber}55`,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
            {prof.avatar_url
              ?<img src={prof.avatar_url} alt="Profil" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
              :<div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',background:T.amber,color:'#000',fontSize:28,fontWeight:800}}>?</div>
            }
          </div>
          <div style={{flex:1,minWidth:180}}>
            <div style={{display:'flex',flexWrap:'wrap',gap:12,marginBottom:8}}>
              {prof.hunting_region&&<span style={{color:T.textSub,fontSize:14}}><span style={{color:T.amber,fontWeight:600}}>Jaktort:</span> {prof.hunting_region}</span>}
              <span style={{color:T.textSub,fontSize:14}}><span style={{color:T.amber,fontWeight:600}}>Erfarenhet:</span> {expLabel[prof.experience_level]||prof.experience_level}</span>
            </div>
            {prof.bio&&<p style={{color:T.textSub,fontSize:13,margin:0,lineHeight:1.5,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{prof.bio}</p>}
          </div>
        </div>

        {/* Stats row */}
        <div style={{display:'flex',flexWrap:'wrap',gap:10,marginBottom:16}}>
          {[
            {label:'Licenser',val:prof.license_urls.length||'0',show:true},
            {label:'Köttbehov',val:prof.meat_need_kg_per_year?prof.meat_need_kg_per_year+' kg/år':'—',show:true},
            {label:'Budget',val:prof.budget_per_season?Number(prof.budget_per_season).toLocaleString('sv-SE')+' kr':'—',show:true},
            {label:'Fällt vilt',val:totalKills>0?totalKills+' st':'0',show:true},
            {label:'Hundar',val:prof.dogs.length>0?prof.dogs.length+' st':'0',show:true},
            {label:'Utrustning',val:prof.equipment.length>0?prof.equipment.length+' st':'0',show:true},
            {label:'Vapen',val:weapons.filter(w=>w.id).length>0?weapons.filter(w=>w.id).length+' st':'0',show:true},
          ].map(s=>(
            <div key={s.label} style={{background:T.surfaceHover,borderRadius:4,padding:'8px 14px',border:`1px solid ${T.border}`,textAlign:'center',minWidth:80}}>
              <div style={{color:T.amber,fontSize:18,fontWeight:700}}>{s.val}</div>
              <div style={{color:T.textMuted,fontSize:11,marginTop:2}}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Preferred game */}
        {prof.preferred_game.length>0&&(
          <div style={{...cS,marginBottom:12,padding:'14px 16px'}}>
            <div style={{color:T.textSub,fontSize:12,marginBottom:8,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.05em'}}>Föredragna vilttyper</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {prof.preferred_game.map(g=>(
                <span key={g} style={{background:T.amberDim,color:T.amber,border:`1px solid ${T.amber}44`,borderRadius:8,padding:'4px 12px',fontSize:12,fontWeight:600,textTransform:'capitalize'}}>{g}</span>
              ))}
            </div>
          </div>
        )}

        {/* Dogs compact */}
        {prof.dogs.length>0&&(
          <div style={{...cS,marginBottom:12,padding:'14px 16px'}}>
            <div style={{color:T.textSub,fontSize:12,marginBottom:10,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.05em'}}>Jakthundar</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:10}}>
              {prof.dogs.map(d=>(
                <div key={d.id} style={{display:'flex',alignItems:'center',gap:8,background:T.surfaceHover,borderRadius:4,padding:'8px 12px',border:`1px solid ${T.border}`}}>
                  {d.image_url&&<img src={d.image_url} alt={d.name} style={{width:32,height:32,borderRadius:'50%',objectFit:'cover'}}/>}
                  <div>
                    <div style={{color:T.text,fontSize:13,fontWeight:600}}>{d.name||'Namnlös'}</div>
                    <div style={{color:T.textMuted,fontSize:11}}>{[d.breed,d.age?d.age+' år':null].filter(Boolean).join(' · ')}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Certifications */}
        {prof.certifications&&(
          <div style={{...cS,padding:'14px 16px'}}>
            <div style={{color:T.textSub,fontSize:12,marginBottom:8,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.05em'}}>Certifikat & behörigheter</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {prof.certifications.split('\n').filter(Boolean).map((c,i)=>(
                <span key={i} style={{background:'rgba(97,216,54,0.1)',color:'#61D836',border:'1px solid rgba(97,216,54,0.2)',borderRadius:3,padding:'3px 10px',fontSize:12}}>{c}</span>
              ))}
            </div>
          </div>
        )}

        {/* P6: fällt i Qvenari (automatiskt, bara man själv) */}
        <MyKillStats/>

        {/* Contracts */}
        <MyContracts />

        {/* Meat orders / escrow */}
        <MyMeatOrdersSummary />
      </div>
    );
  }

  return(
    <div className="view-pad" style={{paddingTop:160,paddingBottom:32,paddingLeft:'clamp(12px,3vw,32px)',paddingRight:'clamp(12px,3vw,32px)',maxWidth:860,margin:'0 auto'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:32,flexWrap:'wrap',gap:12}}>
        <div>
          <h1 style={{color:T.text,fontSize:24,fontWeight:700,margin:0,marginBottom:4}}>Min profil</h1>
          <p style={{color:T.textSub,fontSize:14,margin:0}}>Hantera dina uppgifter, licenser och jakthundar</p>
        </div>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          {err&&<span style={{color:T.danger,fontSize:13}}>{err}</span>}
          {saved&&<span style={{color:T.success,fontSize:13,fontWeight:500}}>Profilen sparad!</span>}
          <button onClick={()=>setEditMode(false)} style={{...bSc,padding:'8px 16px',fontSize:13}}>Stäng</button>
          <button onClick={save} disabled={saving} style={{...bP,opacity:saving?0.6:1,minWidth:120}}>{saving?'Sparar...':'Spara profil'}</button>
        </div>
      </div>

      {user&&onLogout&&(
        <div style={{...cS,marginBottom:20,display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:14,flexWrap:'wrap'}}>
          <div style={{minWidth:0,flex:1}}>
            <div style={{color:T.textSub,fontSize:11,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase',marginBottom:6}}>Konto</div>
            <div style={{color:T.text,fontSize:15,fontWeight:600,wordBreak:'break-word',lineHeight:1.35}}>{user.name||user.email}</div>
            {user.email&&(user.name||'')!==user.email&&<div style={{color:T.textMuted,fontSize:13,marginTop:4,wordBreak:'break-all'}}>{user.email}</div>}
          </div>
          <button type="button" onClick={onLogout} style={{...bSc,padding:'10px 18px',fontSize:13,fontWeight:600,flexShrink:0,alignSelf:'center'}}>Logga ut</button>
        </div>
      )}

      {/* ── PROFILBILD ── */}
      <div style={{...cS,marginBottom:20,display:'flex',alignItems:'center',gap:24,flexWrap:'wrap'}}>
        <div style={{position:'relative',flexShrink:0}}>
          <div style={{width:96,height:96,borderRadius:'50%',overflow:'hidden',background:T.surfaceHover,border:`3px solid ${T.amber}44`,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',position:'relative'}} onClick={()=>avatarRef.current?.click()}>
            {prof.avatar_url
              ?<img src={prof.avatar_url} alt="Profilbild" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
              :<div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',background:T.amber,color:'#000',fontSize:32,fontWeight:800}}>{initials||'?'}</div>
            }
            {uploadingAvatar&&<div style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{width:24,height:24,border:`3px solid ${T.amber}`,borderTopColor:'transparent',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/></div>}
          </div>
        </div>
        <div>
          <div style={{color:T.text,fontSize:15,fontWeight:600,marginBottom:8}}>Profilbild</div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <button type="button" onClick={()=>avatarRef.current?.click()} style={{...bSc,padding:'7px 14px',fontSize:13}}>Ladda upp bild</button>
            <button type="button" onClick={()=>cameraRef.current?.click()} style={{...bSc,padding:'7px 14px',fontSize:13}}>Ta foto</button>
          </div>
          <div style={{color:T.textMuted,fontSize:11,marginTop:6}}>JPG, PNG, WEBP — klicka på bilden för att byta</div>
        </div>
        <input ref={avatarRef} type="file" accept="image/*" style={{display:'none'}} onChange={onAvatarChange}/>
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{display:'none'}} onChange={onAvatarChange}/>
      </div>

      {/* ── GRUNDUPPGIFTER ── */}
      <div style={{...cS,marginBottom:20}}>
        <div style={{color:T.text,fontSize:15,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:`1px solid ${T.border}`}}>Grunduppgifter</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:16}}>
          <div><label style={lS}>Jaktort / Hemort</label><input value={prof.hunting_region} onChange={e=>setProf(p=>({...p,hunting_region:e.target.value}))} placeholder="t.ex. Dalarna, Sörmland" style={iS}/></div>
          <div><label style={lS}>Erfarenhetsnivå</label>
            <select value={prof.experience_level} onChange={e=>setProf(p=>({...p,experience_level:e.target.value}))} style={iS}>
              <option value="nyborjare">Nybörjare</option>
              <option value="medel">Medel</option>
              <option value="erfaren">Erfaren</option>
              <option value="expert">Expert</option>
            </select>
          </div>
          <div><label style={lS}>Köttbehov (kg/år)</label><input type="number" value={prof.meat_need_kg_per_year} onChange={e=>setProf(p=>({...p,meat_need_kg_per_year:e.target.value}))} placeholder="t.ex. 50" style={iS}/></div>
          <div><label style={lS}>Årsbudget (kr)</label><input type="number" value={prof.budget_per_season} onChange={e=>setProf(p=>({...p,budget_per_season:e.target.value}))} placeholder="t.ex. 15000" style={iS}/></div>
        </div>
      </div>

      {/* ── ERFARENHET & BIO ── */}
      <div style={{...cS,marginBottom:20}}>
        <div style={{color:T.text,fontSize:15,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:`1px solid ${T.border}`}}>Om mig & mina erfarenheter</div>
        <textarea value={prof.bio} onChange={e=>setProf(p=>({...p,bio:e.target.value}))} rows={5} placeholder="Berätta om din jaktbakgrund, var du jagar, vad du brinner för. Hur länge har du jagat? Vilken typ av jakt föredrar du?" style={{...iS,resize:'vertical'}}/>
        <div style={{marginTop:16}}>
          <label style={lS}>Certifikat & behörigheter (ett per rad)</label>
          <textarea value={prof.certifications} onChange={e=>setProf(p=>({...p,certifications:e.target.value}))} rows={3} placeholder={'Jägarexamen 2018\nEftersökscertifikat\nSlaktutbildning'} style={{...iS,resize:'vertical'}}/>
        </div>
        <div style={{marginTop:16}}>
          <label style={lS}>Föredragna vilttyper</label>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {ALL_GAME_SPECIES.slice(0,12).map(g=>(
              <button key={g} type="button" onClick={()=>togGame(g)} style={{background:prof.preferred_game.includes(g)?T.amberDim:'transparent',color:prof.preferred_game.includes(g)?T.amber:T.textSub,border:`1px solid ${prof.preferred_game.includes(g)?T.amber:T.border}`,borderRadius:10,padding:'5px 12px',fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit',textTransform:'capitalize'}}>{g}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ── JÄGAREXAMEN ── */}
      <div style={{...cS,marginBottom:20}}>
        <div style={{color:T.text,fontSize:15,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:`1px solid ${T.border}`}}>Jägarexamenbevis</div>
        <div style={{color:T.textSub,fontSize:13,marginBottom:12}}>Ladda upp foto av ditt jägarexamenbevis. OCR tolkar automatiskt namn, datum och examennummer.</div>

        {!hunterExam?(
          <div>
            <input type="file" accept="image/*" onChange={async(e)=>{
              const file=e.target.files?.[0];if(!file)return;
              setHunterExamLoading(true);
              const reader=new FileReader();
              reader.onload=async(ev)=>{
                const dataUrl=ev.target?.result as string;
                try{
                  const url=await uploadFile(file,'images');
                  const result=await api.ocrHunterExam(dataUrl);
                  const d=result.success&&result.data?result.data:{};
                  setHunterExam({
                    imageUrl:url,
                    holderName:d.holderName||'',
                    examDate:d.examDate||'',
                    examNumber:d.examNumber||'',
                    issuedBy:d.issuedBy||'',
                    ocrRaw:JSON.stringify(d,null,2),
                  });
                  if(!result.success)alert(result.error||'OCR misslyckades — fyll i fälten manuellt.');
                }catch{alert('Fel vid uppladdning — försök igen.');}
                setHunterExamLoading(false);
              };
              reader.readAsDataURL(file);
            }} style={{display:'none'}} id="hunterExamInput"/>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              <button type="button" onClick={()=>document.getElementById('hunterExamInput')?.click()} disabled={hunterExamLoading} style={{...bSc,padding:'8px 16px',fontSize:13,opacity:hunterExamLoading?0.6:1}}>
                {hunterExamLoading?'Läser bevis...':'Ladda upp jägarexamenbevis'}
              </button>
              <button type="button" onClick={()=>{const i=document.createElement('input');i.type='file';i.accept='image/*';i.capture='environment';i.onchange=async(ev:Event)=>{const t=ev.target as HTMLInputElement;const file=t.files?.[0];if(!file)return;setHunterExamLoading(true);const reader=new FileReader();reader.onload=async(rev)=>{const dataUrl=rev.target?.result as string;try{const url=await uploadFile(file,'images');const result=await api.ocrHunterExam(dataUrl);const d=result.success&&result.data?result.data:{};setHunterExam({imageUrl:url,holderName:d.holderName||'',examDate:d.examDate||'',examNumber:d.examNumber||'',issuedBy:d.issuedBy||'',ocrRaw:JSON.stringify(d,null,2)});if(!result.success)alert(result.error||'OCR misslyckades');}catch{alert('Fel vid uppladdning');}setHunterExamLoading(false);};reader.readAsDataURL(file);};i.click()}} disabled={hunterExamLoading} style={{...bSc,padding:'8px 16px',fontSize:13,opacity:hunterExamLoading?0.6:1}}>
                Fotografera bevis
              </button>
            </div>
            <div style={{color:T.textMuted,fontSize:13,fontStyle:'italic',marginTop:12}}>Inget jägarexamenbevis uppladdat ännu</div>
          </div>
        ):(
          <div>
            {/* Visual license card with image + OCR text */}
            <div style={{background:'linear-gradient(135deg, #1a2a1a 0%, #243024 100%)',border:`2px solid ${T.amber}44`,borderRadius:7,overflow:'hidden',marginBottom:16}}>
              <div style={{display:'flex',gap:0,flexWrap:'wrap'}}>
                {/* Image side */}
                <div style={{flex:'1 1 240px',minWidth:200,maxWidth:360,position:'relative'}}>
                  <img src={hunterExam.imageUrl} alt="Jägarexamenbevis" style={{width:'100%',height:'100%',minHeight:200,objectFit:'cover',display:'block'}}/>
                  <div style={{position:'absolute',top:8,left:8,background:'rgba(238,143,9,0.9)',color:'#000',padding:'3px 10px',borderRadius:3,fontSize:11,fontWeight:700}}>JÄGAREXAMEN</div>
                </div>
                {/* Text side — OCR data */}
                <div style={{flex:'1 1 280px',padding:'20px 24px',display:'flex',flexDirection:'column',justifyContent:'center',gap:12}}>
                  <div style={{fontSize:18,fontWeight:800,color:T.amber,letterSpacing:'-0.02em'}}>Jägarexamenbevis</div>
                  {([
                    {label:'Innehavare',val:hunterExam.holderName},
                    {label:'Examensdatum',val:hunterExam.examDate},
                    {label:'Examennummer',val:hunterExam.examNumber},
                    {label:'Utfärdat av',val:hunterExam.issuedBy},
                  ] as {label:string;val:string}[]).map(({label,val})=>val?(
                    <div key={label}>
                      <div style={{fontSize:10,color:T.textMuted,textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:2}}>{label}</div>
                      <div style={{fontSize:14,color:T.text,fontWeight:600}}>{val}</div>
                    </div>
                  ):null)}
                </div>
              </div>
            </div>
            {/* Edit fields */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
              <div><label style={lS}>Innehavare</label><input value={hunterExam.holderName} onChange={e=>setHunterExam(p=>p?{...p,holderName:e.target.value}:p)} style={iS}/></div>
              <div><label style={lS}>Examensdatum</label><input value={hunterExam.examDate} onChange={e=>setHunterExam(p=>p?{...p,examDate:e.target.value}:p)} placeholder="ÅÅÅÅ-MM-DD" style={iS}/></div>
              <div><label style={lS}>Examennummer</label><input value={hunterExam.examNumber} onChange={e=>setHunterExam(p=>p?{...p,examNumber:e.target.value}:p)} style={iS}/></div>
              <div><label style={lS}>Utfärdat av</label><input value={hunterExam.issuedBy} onChange={e=>setHunterExam(p=>p?{...p,issuedBy:e.target.value}:p)} style={iS}/></div>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button type="button" onClick={()=>{if(confirm('Ta bort jägarexamenbevis?'))setHunterExam(null)}} style={{background:'rgba(255,80,80,0.15)',color:'#f88',border:'1px solid rgba(255,80,80,0.3)',borderRadius:3,padding:'6px 14px',fontSize:12,cursor:'pointer'}}>Ta bort bevis</button>
            </div>
          </div>
        )}
        <HunterApprovalStatus hasLocalExam={!!hunterExam}/>
      </div>

      {/* ── VAPENLICENSER ── */}
      <div style={{...cS,marginBottom:20}}>
        <div style={{color:T.text,fontSize:15,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:`1px solid ${T.border}`}}>Vapenlicenser</div>
        <div style={{color:T.textSub,fontSize:13,marginBottom:12}}>Ladda upp foton eller PDF av dina vapenlicenser. Dessa är privata och visas bara för jaktarrangörer när du bokar.</div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:16}}>
          <button type="button" onClick={()=>licenseRef.current?.click()} disabled={uploadingLicense} style={{...bSc,padding:'8px 16px',fontSize:13,opacity:uploadingLicense?0.6:1}}>
            {uploadingLicense?'Laddar upp...':'Ladda upp licens'}
          </button>
          <button type="button" onClick={()=>{const i=document.createElement('input');i.type='file';i.accept='image/*';i.capture='environment';i.onchange=(e:Event)=>{const t=e.target as HTMLInputElement;if(t.files?.length){onLicenseChange({target:t,currentTarget:t,bubbles:false,cancelable:false,defaultPrevented:false,eventPhase:0,isTrusted:true,preventDefault:()=>{},isDefaultPrevented:()=>false,stopPropagation:()=>{},isPropagationStopped:()=>false,stopImmediatePropagation:()=>{},persist:()=>{},nativeEvent:new Event('change'),type:'change',timeStamp:0} as unknown as React.ChangeEvent<HTMLInputElement>)}};i.click()}} style={{...bSc,padding:'8px 16px',fontSize:13}}>Fotografera licens</button>
        </div>
        <input ref={licenseRef} type="file" accept="image/*,.pdf" multiple style={{display:'none'}} onChange={onLicenseChange}/>
        {prof.license_urls.length>0&&(
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',gap:10}}>
            {prof.license_urls.map((url,i)=>(
              <div key={url} style={{position:'relative',background:T.surfaceHover,borderRadius:4,overflow:'hidden',border:`1px solid ${T.border}`}}>
                {url.endsWith('.pdf')
                  ?<div style={{height:80,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:4}}><span style={{fontSize:24}}>📄</span><span style={{color:T.textSub,fontSize:11}}>PDF {i+1}</span></div>
                  :<img src={url} alt={`Licens ${i+1}`} style={{width:'100%',height:80,objectFit:'cover',display:'block'}}/>
                }
                <button onClick={()=>rmLicense(url)} style={{position:'absolute',top:4,right:4,background:'rgba(0,0,0,0.7)',border:'none',color:'#fff',borderRadius:'50%',width:20,height:20,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,lineHeight:1}}>×</button>
              </div>
            ))}
          </div>
        )}
        {prof.license_urls.length===0&&<div style={{color:T.textMuted,fontSize:13,fontStyle:'italic'}}>Inga licenser uppladdade ännu</div>}
      </div>

      {/* P2: uppgifter som jaktmarker kontrollerar vid antagning */}
      <AdmissionProfileFields/>

      {/* ── VILTSTATISTIK ── */}
      <div style={{...cS,marginBottom:20}}>
        <div style={{color:T.text,fontSize:15,fontWeight:700,marginBottom:4,paddingBottom:10,borderBottom:`1px solid ${T.border}`}}>Avskjutningsstatistik</div>
        <div style={{color:T.textSub,fontSize:13,marginBottom:16}}>Hur många vilt av varje slag har du skjutit totalt under din jakttid?</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:10}}>
          {ALL_GAME_SPECIES.map(sp=>{
            const count=prof.game_kills[sp]||0;
            return(
              <div key={sp} style={{background:T.surfaceHover,borderRadius:4,padding:'10px 12px',border:`1px solid ${count>0?T.amber+'44':T.border}`}}>
                <div style={{color:count>0?T.amber:T.textSub,fontSize:12,fontWeight:600,marginBottom:8,textTransform:'capitalize'}}>{sp}</div>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <button type="button" onClick={()=>setKill(sp,count-1)} style={{width:26,height:26,borderRadius:3,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:16,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'inherit',lineHeight:1}}>−</button>
                  <span style={{flex:1,textAlign:'center',color:T.text,fontSize:16,fontWeight:700,minWidth:24}}>{count}</span>
                  <button type="button" onClick={()=>setKill(sp,count+1)} style={{width:26,height:26,borderRadius:3,background:T.surface,border:`1px solid ${T.border}`,color:T.text,fontSize:16,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'inherit',lineHeight:1}}>+</button>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{marginTop:12,color:T.textMuted,fontSize:12}}>Totalt: {Object.values(prof.game_kills).reduce((a,b)=>a+b,0)} djur</div>
      </div>

      {/* ── JAKTHUNDAR ── */}
      <div style={{...cS,marginBottom:24}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,paddingBottom:10,borderBottom:`1px solid ${T.border}`}}>
          <div style={{color:T.text,fontSize:15,fontWeight:700}}>Jakthundar</div>
          <button type="button" onClick={addDog} style={{...bP,padding:'6px 14px',fontSize:13}}>+ Lägg till hund</button>
        </div>
        {prof.dogs.length===0&&<div style={{color:T.textMuted,fontSize:13,fontStyle:'italic'}}>Inga hundar registrerade ännu</div>}
        {prof.dogs.map((dog,i)=>(
          <div key={dog.id} style={{background:T.surfaceHover,borderRadius:5,padding:16,marginBottom:12,border:`1px solid ${T.border}`,position:'relative'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
              <div style={{color:T.amber,fontSize:13,fontWeight:700}}>Hund {i+1}{dog.name?' — '+dog.name:''}</div>
              <button type="button" onClick={()=>rmDog(dog.id)} style={{background:'none',border:'none',color:T.danger,cursor:'pointer',fontSize:18,padding:'0 4px',lineHeight:1}}>×</button>
            </div>
            <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
              {/* Dog image */}
              <div style={{flexShrink:0}}>
                <input type="file" accept="image/*" ref={el=>{dogImgRef.current[dog.id]=el}} onChange={e=>handleDogImg(dog.id,e)} style={{display:'none'}}/>
                <div onClick={()=>dogImgRef.current[dog.id]?.click()} style={{width:100,height:100,borderRadius:6,overflow:'hidden',cursor:'pointer',border:`2px dashed ${T.border}`,display:'flex',alignItems:'center',justifyContent:'center',background:T.surface,position:'relative'}}>
                  {uploadingDogImg[dog.id]?<span style={{color:T.textMuted,fontSize:11}}>Laddar...</span>:dog.image_url?(
                    <img src={dog.image_url} alt={dog.name||'Hund'} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                  ):(
                    <div style={{textAlign:'center',color:T.textMuted,fontSize:11,lineHeight:1.3}}>
                      <div style={{fontSize:24,marginBottom:2}}>+</div>Bild
                    </div>
                  )}
                </div>
                {dog.image_url&&<button type="button" onClick={()=>updDog(dog.id,'image_url','')} style={{background:'none',border:'none',color:T.danger,cursor:'pointer',fontSize:11,marginTop:4,display:'block',width:'100%',textAlign:'center'}}>Ta bort</button>}
              </div>
              {/* Dog fields */}
              <div style={{flex:1,minWidth:200}}>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:12}}>
                  <div><label style={lS}>Namn</label><input value={dog.name} onChange={e=>updDog(dog.id,'name',e.target.value)} placeholder="t.ex. Reko" style={iS}/></div>
                  <div><label style={lS}>Ras</label><input value={dog.breed} onChange={e=>updDog(dog.id,'breed',e.target.value)} placeholder="t.ex. Drever" style={iS}/></div>
                  <div><label style={lS}>Ålder (år)</label><input type="number" value={dog.age} onChange={e=>updDog(dog.id,'age',e.target.value)} placeholder="t.ex. 4" style={iS}/></div>
                  <div><label style={lS}>Drevlängd (km)</label><input type="number" value={dog.trail_km} onChange={e=>updDog(dog.id,'trail_km',e.target.value)} placeholder="t.ex. 12" style={iS}/></div>
                </div>
                <div style={{marginTop:12}}><label style={lS}>Anteckningar</label><input value={dog.notes} onChange={e=>updDog(dog.id,'notes',e.target.value)} placeholder="Meriter, certifikat, spetsegenskaper..." style={iS}/></div>
                {/* Garmin tracker link */}
                <div style={{marginTop:12}}>
                  <label style={lS}>Garmin-spårare (GPS-tracker)</label>
                  {garminDevicesList.length>0?(
                    <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                      <select value={dog.garmin_imei||''} onChange={async e=>{const imei=e.target.value;updDog(dog.id,'garmin_imei',imei);if(imei&&token){const dev=garminDevicesList.find(d=>d.imei===imei);try{await apiFetch(`/api/profile/integrations/garmin/devices`,{method:'POST',headers:{'Content-Type':'application/json',...bearerHeaders(token),'X-Api-Key':'dev-secret'},body:JSON.stringify({imei,dogId:dog.id,dogName:dog.name||'Okänd hund',deviceName:dev?.device_name,deviceModel:dev?.device_model})});}catch{}}}} style={{...iS,flex:1}}>
                        <option value="">— Ingen kopplad spårare —</option>
                        {garminDevicesList.map((dev:any)=>(
                          <option key={dev.imei} value={dev.imei}>{dev.device_name||dev.imei}{dev.device_model?` (${dev.device_model})`:''}</option>
                        ))}
                      </select>
                      {dog.garmin_imei&&(
                        <div style={{color:'#61D836',fontSize:11,display:'flex',alignItems:'center',gap:4}}>
                          <span>●</span>
                          <span>Länkad</span>
                        </div>
                      )}
                    </div>
                  ):(
                    <div style={{color:T.textMuted,fontSize:12,fontStyle:'italic'}}>
                      Koppla Garmin inReach under Integrationer nedan för att aktivera GPS-spårning per hund.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── JAKT & VAPEN GARDEROB ── */}
      <div style={{...cS,marginBottom:24}}>
        <div style={{marginBottom:16,paddingBottom:10,borderBottom:`1px solid ${T.border}`}}>
          <div style={{color:T.text,fontSize:15,fontWeight:700,marginBottom:12}}>Jakt &amp; vapen garderob</div>
          <div style={{display:'flex',gap:8}}>
            <button type="button" onClick={()=>setWeaponGardTab('utrustning')} style={{padding:'6px 18px',borderRadius:10,border:'none',background:weaponGardTab==='utrustning'?T.amber:'rgba(255,255,255,0.1)',color:weaponGardTab==='utrustning'?'#000':'#ccc',fontWeight:600,cursor:'pointer',fontSize:13}}>Utrustning</button>
            <button type="button" onClick={()=>setWeaponGardTab('vapen')} style={{padding:'6px 18px',borderRadius:10,border:'none',background:weaponGardTab==='vapen'?T.amber:'rgba(255,255,255,0.1)',color:weaponGardTab==='vapen'?'#000':'#ccc',fontWeight:600,cursor:'pointer',fontSize:13}}>Vapen</button>
          </div>
        </div>

        {/* ── Utrustning sub-tab ── */}
        {weaponGardTab==='utrustning'&&<>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
          <button type="button" onClick={()=>{if(showEquipLabels){setShowEquipLabels(false);}else{openEquipLabels();}}} style={{background:'rgba(238,143,9,0.15)',border:`1px solid ${T.amber}`,color:T.amber,borderRadius:4,padding:'6px 14px',fontSize:13,fontWeight:600,cursor:'pointer'}}>
            🏷️ Skriv ut etiketter för utrustning &amp; vapen
          </button>
          <button type="button" onClick={addEquipment} style={{...bP,padding:'6px 14px',fontSize:13}}>+ Lägg till utrustning</button>
        </div>
        {/* Inline label printer for equipment/weapons */}
        {showEquipLabels&&(
          <div style={{marginBottom:20,border:`1px solid ${T.border}`,borderRadius:6,overflow:'hidden'}}>
            <LabelPrinter
              user={null} isAdmin={false}
              preloadedLabels={equipLabelData.length?equipLabelData:undefined}
              initialTemplateType="utrustning"
              embedded={true}
              onClose={()=>setShowEquipLabels(false)}
              defaultMarkName=""
            />
          </div>
        )}
        {prof.equipment.length===0&&<div style={{color:T.textMuted,fontSize:13,fontStyle:'italic'}}>Ingen utrustning registrerad ännu</div>}
        {prof.equipment.map((eq,i)=>(
          <div key={eq.id} style={{background:T.surfaceHover,borderRadius:5,padding:16,marginBottom:12,border:`1px solid ${T.border}`,position:'relative'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
              <div style={{color:T.amber,fontSize:13,fontWeight:700}}>Utrustning {i+1}{eq.name?' — '+eq.name:''}</div>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                {eqSaving[eq.id]&&<span style={{color:T.textMuted,fontSize:11}}>Sparar...</span>}
                <button type="button" onClick={()=>rmEquipment(eq.id)} style={{background:'none',border:'none',color:T.danger,cursor:'pointer',fontSize:18,padding:'0 4px',lineHeight:1}}>x</button>
              </div>
            </div>
            {/* Image upload */}
            <div style={{display:'flex',gap:16,marginBottom:14,flexWrap:'wrap'}}>
              <div style={{width:120,height:80,borderRadius:4,overflow:'hidden',background:T.surface,border:`1px solid ${T.border}`,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}} onClick={()=>{const inp=document.createElement('input');inp.type='file';inp.accept='image/*';inp.onchange=(ev:Event)=>{const t=ev.target as HTMLInputElement;if(t.files?.[0]){const fd=new FormData();fd.append('file',t.files[0]);apiFetch('/api/upload',{method:'POST',body:fd}).then(r=>r.json()).then((d:any)=>{updEquipment(eq.id,'image_url',d.url)}).catch(()=>{})}};inp.click()}}>
                {eq.image_url
                  ?<img src={eq.image_url} alt={eq.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                  :<span style={{color:T.textMuted,fontSize:11,textAlign:'center',padding:4}}>Klicka för bild</span>
                }
              </div>
              <div style={{flex:1,minWidth:200}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <div><label style={lS}>Namn</label><input value={eq.name} onChange={e=>updEquipment(eq.id,'name',e.target.value)} placeholder="t.ex. Swarovski EL 10x42" style={iS}/></div>
                  <div><label style={lS}>Kategori</label>
                    <select value={eq.category} onChange={e=>updEquipment(eq.id,'category',e.target.value)} style={iS}>
                      {EQ_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div style={{marginBottom:10}}><label style={lS}>Beskrivning</label><textarea value={eq.description} onChange={e=>updEquipment(eq.id,'description',e.target.value)} rows={2} placeholder="Beskriv utrustningen..." style={{...iS,resize:'vertical'}}/></div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:10,marginBottom:12}}>
              <div><label style={lS}>Skick</label>
                <select value={eq.condition} onChange={e=>updEquipment(eq.id,'condition',e.target.value)} style={iS}>
                  {EQ_CONDITIONS.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{display:'flex',alignItems:'flex-end',gap:16,padding:'0 0 2px'}}>
                <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:13,color:T.text}}>
                  <input type="checkbox" checked={eq.for_rent} onChange={e=>{updEquipment(eq.id,'for_rent',e.target.checked)}} style={{accentColor:T.amber}}/>
                  Hyr ut
                </label>
                <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:13,color:T.text}}>
                  <input type="checkbox" checked={eq.for_sale} onChange={e=>{updEquipment(eq.id,'for_sale',e.target.checked)}} style={{accentColor:T.amber}}/>
                  Sälj
                </label>
              </div>
            </div>
            {(eq.for_rent||eq.for_sale)&&(
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:10,marginBottom:12}}>
                {eq.for_rent&&<div><label style={lS}>Hyrpris (kr/mån)</label><input type="number" value={eq.rent_price} onChange={e=>updEquipment(eq.id,'rent_price',e.target.value)} placeholder="t.ex. 500" style={iS}/></div>}
                {eq.for_sale&&<div><label style={lS}>Säljpris (kr)</label><input type="number" value={eq.sale_price} onChange={e=>updEquipment(eq.id,'sale_price',e.target.value)} placeholder="t.ex. 15000" style={iS}/></div>}
              </div>
            )}
            {eq.qregistry_object_id&&<div style={{color:T.textMuted,fontSize:11,marginBottom:8}}>Qregistry: {eq.qregistry_object_id}</div>}
            <div style={{display:'flex',justifyContent:'flex-end'}}>
              <button type="button" onClick={()=>saveEquipment(eq)} disabled={!!eqSaving[eq.id]||!eq.name.trim()} style={{...bP,padding:'6px 16px',fontSize:12,opacity:(!eq.name.trim()||eqSaving[eq.id])?0.5:1}}>{eqSaving[eq.id]?'Sparar...':'Spara utrustning'}</button>
            </div>
          </div>
        ))}
        </>}

        {/* ── Vapen sub-tab ── */}
        {weaponGardTab==='vapen'&&(
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <span style={{color:T.textSub,fontSize:13}}>Lägg till dina vapen med licensfoto för automatisk registrering.</span>
              <button type="button" onClick={()=>{setEditingWeapon({id:'',name:'',weaponType:'Gevär',manufacturer:'',model:'',serialNumber:'',caliber:'',licenseNumber:'',licenseType:'Jakt',licenseHolder:'',licenseExpires:'',licenseImageUrl:'',licenseImageUrlBack:'',description:'',image_url:'',condition:'Bra skick',for_rent:false,rent_price:'0',for_sale:false,sale_price:'0',licenses:[]});setShowWeaponForm(true);}} style={{background:'#EE8F09',color:'#fff',border:'none',borderRadius:3,padding:'8px 16px',fontWeight:600,cursor:'pointer',fontSize:13}}>
                + Lägg till vapen
              </button>
            </div>

            {weapons.filter(w=>w.id).map(w=>(
              <div key={w.id} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:5,padding:14,marginBottom:12}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:15,color:'#EE8F09',marginBottom:4}}>{w.name}</div>
                    <div style={{fontSize:12,color:T.textSub,display:'flex',gap:16,flexWrap:'wrap'}}>
                      <span>Typ: {w.weaponType}</span>
                      {w.manufacturer&&<span>Märke: {w.manufacturer}</span>}
                      {w.model&&<span>Modell: {w.model}</span>}
                      {w.serialNumber&&<span style={{background:'rgba(238,143,9,0.2)',padding:'1px 6px',borderRadius:1}}>Serienr: {w.serialNumber}</span>}
                      {w.caliber&&<span>Kaliber: {w.caliber}</span>}
                      {w.licenseNumber&&<span>Licens: {w.licenseNumber}</span>}
                    </div>
                    {/* License visual card */}
                    {(w.licenseImageUrl||w.licenseImageUrlBack)&&(
                      <div style={{marginTop:10,background:T.surfaceHover,border:`1px solid ${T.amber}33`,borderRadius:5,overflow:'hidden'}}>
                        <div style={{display:'flex',gap:0}}>
                          {w.licenseImageUrl&&(
                            <div style={{flex:'1 1 120px',position:'relative'}}>
                              <div style={{position:'absolute',top:4,left:4,background:'rgba(238,143,9,0.85)',color:'#000',padding:'2px 6px',borderRadius:2,fontSize:9,fontWeight:700}}>FRAMSIDA</div>
                              <img src={w.licenseImageUrl} alt="Framsida" style={{width:'100%',height:100,objectFit:'cover',display:'block'}}/>
                            </div>
                          )}
                          {w.licenseImageUrlBack&&(
                            <div style={{flex:'1 1 120px',position:'relative'}}>
                              <div style={{position:'absolute',top:4,left:4,background:'rgba(238,143,9,0.85)',color:'#000',padding:'2px 6px',borderRadius:2,fontSize:9,fontWeight:700}}>BAKSIDA</div>
                              <img src={w.licenseImageUrlBack} alt="Baksida" style={{width:'100%',height:100,objectFit:'cover',display:'block'}}/>
                            </div>
                          )}
                          <div style={{flex:'1 1 160px',padding:'10px 14px',display:'flex',flexDirection:'column',justifyContent:'center',gap:4}}>
                            <div style={{fontSize:11,fontWeight:800,color:T.amber,textTransform:'uppercase',letterSpacing:'0.05em'}}>Vapenlicens</div>
                            {w.licenseHolder&&<div style={{fontSize:12,color:T.text}}>{w.licenseHolder}</div>}
                            {w.licenseNumber&&<div style={{fontSize:11,color:T.textSub}}>Nr: {w.licenseNumber}</div>}
                            {w.licenseExpires&&<div style={{fontSize:11,color:T.textSub}}>Giltig till: {w.licenseExpires}</div>}
                          </div>
                        </div>
                      </div>
                    )}
                    <div style={{marginTop:6,display:'flex',gap:8}}>
                      {w.for_rent&&<span style={{background:'#61D836',color:'#000',borderRadius:2,padding:'2px 8px',fontSize:11,fontWeight:600}}>Uthyres {w.rent_price} kr/mån</span>}
                      {w.for_sale&&<span style={{background:'#00A2FF',color:'#fff',borderRadius:2,padding:'2px 8px',fontSize:11,fontWeight:600}}>Till salu {w.sale_price} kr</span>}
                    </div>
                  </div>
                  <div style={{display:'flex',gap:8,marginLeft:12}}>
                    <button type="button" onClick={()=>{setEditingWeapon(w);setShowWeaponForm(true);}} style={{background:T.surfaceHover,color:T.text,border:`1px solid ${T.border}`,borderRadius:3,padding:'6px 12px',cursor:'pointer',fontSize:12}}>Redigera</button>
                    {w.for_rent&&(
                      <button type="button" onClick={()=>{setLoanFormWeapon(w);setLoanForm({weaponId:w.id,borrowerName:'',borrowerEmail:'',borrowerPhone:'',borrowerPhoneMobile:'',borrowerAddress:'',borrowerPostalCode:'',borrowerCity:'',borrowerPersonnummer:'',purpose:'Jakt',loanStart:'',loanEnd:'',notes:''});}} style={{background:'#EE8F09',color:'#fff',border:'none',borderRadius:3,padding:'6px 12px',cursor:'pointer',fontSize:12,fontWeight:600}}>
                        Hyr ut — blankett 551.26
                      </button>
                    )}
                    <button type="button" onClick={async()=>{if(confirm('Ta bort vapnet?')){await api.deleteWeapon(w.id);setWeapons(p=>p.filter(x=>x.id!==w.id));}}} style={{background:'rgba(255,80,80,0.15)',color:'#f88',border:'1px solid rgba(255,80,80,0.3)',borderRadius:3,padding:'6px 10px',cursor:'pointer',fontSize:12}}>x</button>
                  </div>
                </div>
              </div>
            ))}

            {weapons.filter(w=>w.id).length===0&&(
              <div style={{color:T.textMuted,textAlign:'center',padding:32,background:T.surfaceHover,borderRadius:5,border:`2px dashed ${T.border}`}}>
                Inga vapen registrerade ännu. Lägg till ditt första vapen med licensfoto.
              </div>
            )}

            {/* Weapon edit modal */}
            {showWeaponForm&&editingWeapon&&(
  <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:3000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
    <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:7,padding:24,width:'100%',maxWidth:640,maxHeight:'90vh',overflowY:'auto'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <h3 style={{margin:0,color:'#EE8F09',fontSize:18}}>{editingWeapon.id?'Redigera vapen':'Nytt vapen'}</h3>
        <button type="button" onClick={()=>{setShowWeaponForm(false);setEditingWeapon(null);setCropState(null);setWeaponSavedId(null);}} style={{background:'none',border:'none',color:T.textSub,fontSize:22,cursor:'pointer'}}>×</button>
      </div>

      {/* ── Weapon basic fields ─────────────────────────── */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
        {([
          {label:'Vapennamn *',key:'name',placeholder:'t.ex. Tikka T3x'},
          {label:'Vapentyp',key:'weaponType',placeholder:'Gevär, Hagelgevär, Pistol...'},
          {label:'Tillverkare',key:'manufacturer',placeholder:'Tikka, Browning...'},
          {label:'Modell',key:'model',placeholder:'T3x, BPS...'},
          {label:'Serienummer (helgevär)',key:'serialNumber',placeholder:'ABC12345'},
          {label:'Kaliber',key:'caliber',placeholder:'.308, 30-06, 12/76...'},
        ] as {label:string;key:keyof WeaponEntry;placeholder:string}[]).map(({label,key,placeholder})=>(
          <div key={key as string}>
            <label style={{display:'block',fontSize:11,color:T.textSub,marginBottom:4}}>{label}</label>
            <input value={String((editingWeapon as any)[key]||'')} onChange={e=>setEditingWeapon(p=>p?{...p,[key]:e.target.value}:p)}
              placeholder={placeholder}
              style={{width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:3,padding:'7px 10px',color:T.text,fontSize:13,boxSizing:'border-box'}}/>
          </div>
        ))}
      </div>
      <div style={{marginBottom:16}}>
        <label style={{display:'block',fontSize:11,color:T.textSub,marginBottom:4}}>Beskrivning</label>
        <textarea value={editingWeapon.description||''} onChange={e=>setEditingWeapon(p=>p?{...p,description:e.target.value}:p)}
          placeholder="Noteringar, historik..."
          style={{width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:3,padding:'7px 10px',color:T.text,fontSize:13,minHeight:60,resize:'vertical',boxSizing:'border-box'}}/>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:20}}>
        <div>
          <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,color:T.text}}>
            <input type="checkbox" checked={editingWeapon.for_rent} onChange={e=>setEditingWeapon(p=>p?{...p,for_rent:e.target.checked}:p)}/>
            Hyr ut
          </label>
          {editingWeapon.for_rent&&<input type="number" value={editingWeapon.rent_price} onChange={e=>setEditingWeapon(p=>p?{...p,rent_price:e.target.value}:p)} placeholder="kr/mån" style={{marginTop:6,width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:3,padding:'7px 10px',color:T.text,fontSize:13,boxSizing:'border-box'}}/>}
        </div>
        <div>
          <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,color:T.text}}>
            <input type="checkbox" checked={editingWeapon.for_sale} onChange={e=>setEditingWeapon(p=>p?{...p,for_sale:e.target.checked}:p)}/>
            Till salu
          </label>
          {editingWeapon.for_sale&&<input type="number" value={editingWeapon.sale_price} onChange={e=>setEditingWeapon(p=>p?{...p,sale_price:e.target.value}:p)} placeholder="Pris (kr)" style={{marginTop:6,width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:3,padding:'7px 10px',color:T.text,fontSize:13,boxSizing:'border-box'}}/>}
        </div>
      </div>

      {/* ── LICENSER ───────────────────────────────────── */}
      <div style={{borderTop:`1px solid ${T.border}`,paddingTop:16,marginBottom:16}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
          <div style={{fontSize:14,fontWeight:700,color:'#EE8F09'}}>Licenser ({editingWeapon.licenses.length})</div>
          <button type="button" onClick={()=>{
            const newLic:WeaponLicenseEntry={id:'',partType:'Helgevär',serialNumber:'',licenseNumber:'',licenseType:'Jakt',licenseHolder:'',licenseExpires:'',licenseImageUrl:'',licenseImageUrlBack:'',caliber:'',notes:''};
            setEditingWeapon(p=>p?{...p,licenses:[...p.licenses,newLic]}:p);
            setEditingLicenseIdx(editingWeapon.licenses.length);
            setEditingLicense(newLic);
          }} style={{background:'rgba(238,143,9,0.15)',color:'#EE8F09',border:'1px solid rgba(238,143,9,0.4)',borderRadius:4,padding:'6px 14px',fontSize:12,fontWeight:600,cursor:'pointer'}}>
            + Lägg till licens
          </button>
        </div>

        {editingWeapon.licenses.length===0&&(
          <div style={{color:T.textMuted,fontSize:13,padding:'12px 0',textAlign:'center',background:T.surfaceHover,borderRadius:4,border:`1px dashed ${T.border}`}}>
            Inga licenser tillagda. En licens kan avse hela vapnet, pipa, låda eller slutstycke.
          </div>
        )}

        {editingWeapon.licenses.map((lic,idx)=>(
          <div key={idx} style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:5,padding:12,marginBottom:10}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
              <div style={{fontWeight:600,fontSize:13,color:T.text}}>
                {lic.partType||'Licens'} {lic.licenseNumber?`· ${lic.licenseNumber}`:''}
              </div>
              <div style={{display:'flex',gap:8}}>
                <button type="button" onClick={()=>{setEditingLicenseIdx(idx);setEditingLicense({...lic});}} style={{background:'rgba(238,143,9,0.15)',color:'#EE8F09',border:'1px solid rgba(238,143,9,0.3)',borderRadius:3,padding:'4px 10px',fontSize:11,cursor:'pointer'}}>
                  Redigera
                </button>
                <button type="button" onClick={()=>setEditingWeapon(p=>p?{...p,licenses:p.licenses.filter((_,i)=>i!==idx)}:p)} style={{background:'rgba(200,30,30,0.15)',color:'#e05050',border:'1px solid rgba(200,30,30,0.3)',borderRadius:3,padding:'4px 10px',fontSize:11,cursor:'pointer'}}>
                  Ta bort
                </button>
              </div>
            </div>
            <div style={{fontSize:11,color:T.textSub,display:'flex',flexWrap:'wrap',gap:'4px 16px'}}>
              {lic.serialNumber&&<span>Serienr: {lic.serialNumber}</span>}
              {lic.caliber&&<span>Kaliber: {lic.caliber}</span>}
              {lic.licenseHolder&&<span>Innehavare: {lic.licenseHolder}</span>}
              {lic.licenseExpires&&<span>Giltig t.o.m.: {lic.licenseExpires}</span>}
            </div>
            {(lic.licenseImageUrl||lic.licenseImageUrlBack)&&(
              <div style={{display:'flex',gap:8,marginTop:8}}>
                {lic.licenseImageUrl&&<img src={lic.licenseImageUrl} alt="Framsida" style={{height:60,borderRadius:3,border:`1px solid ${T.border}`,objectFit:'cover'}}/>}
                {lic.licenseImageUrlBack&&<img src={lic.licenseImageUrlBack} alt="Baksida" style={{height:60,borderRadius:3,border:`1px solid ${T.border}`,objectFit:'cover'}}/>}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Save buttons ───────────────────────────────── */}
      {weaponSavedId?(
        <div style={{textAlign:'center',padding:16,background:'rgba(97,216,54,0.1)',borderRadius:5,border:'1px solid rgba(97,216,54,0.3)'}}>
          <div style={{fontSize:15,fontWeight:700,color:'#61D836',marginBottom:12}}>Vapen sparat!</div>
          <div style={{display:'flex',gap:12,justifyContent:'center'}}>
            <button type="button" onClick={()=>{setShowWeaponForm(false);setEditingWeapon(null);setWeaponSavedId(null);}} style={{background:T.surfaceHover,color:T.text,border:`1px solid ${T.border}`,borderRadius:4,padding:'10px 20px',cursor:'pointer',fontSize:13}}>Klar</button>
            <button type="button" onClick={()=>{
              setWeaponSavedId(null);
              setEditingWeapon({id:'',name:'',weaponType:'Gevär',manufacturer:'',model:'',serialNumber:'',caliber:'',licenseNumber:'',licenseType:'Jakt',licenseHolder:'',licenseExpires:'',licenseImageUrl:'',licenseImageUrlBack:'',description:'',image_url:'',condition:'Bra skick',for_rent:false,rent_price:'0',for_sale:false,sale_price:'0',licenses:[]});
            }} style={{background:'#EE8F09',color:'#fff',border:'none',borderRadius:4,padding:'10px 20px',fontWeight:700,cursor:'pointer',fontSize:13}}>
              + Lägg till fler vapen
            </button>
          </div>
        </div>
      ):(
        <div style={{display:'flex',gap:12,justifyContent:'flex-end'}}>
          <button type="button" onClick={()=>{setShowWeaponForm(false);setEditingWeapon(null);setCropState(null);}} style={{background:T.surfaceHover,color:T.textSub,border:`1px solid ${T.border}`,borderRadius:4,padding:'10px 20px',cursor:'pointer'}}>Avbryt</button>
          <button type="button" onClick={async()=>{
            if(!editingWeapon.name){alert('Vapennamn krävs');return;}
            try{
              const payload={
                name:editingWeapon.name,weaponType:editingWeapon.weaponType,
                manufacturer:editingWeapon.manufacturer,model:editingWeapon.model,
                serialNumber:editingWeapon.serialNumber,caliber:editingWeapon.caliber,
                licenseNumber:editingWeapon.licenses[0]?.licenseNumber||editingWeapon.licenseNumber,
                licenseType:editingWeapon.licenses[0]?.licenseType||editingWeapon.licenseType,
                licenseHolder:editingWeapon.licenses[0]?.licenseHolder||editingWeapon.licenseHolder,
                licenseExpires:editingWeapon.licenses[0]?.licenseExpires||editingWeapon.licenseExpires,
                licenseImageUrl:editingWeapon.licenses[0]?.licenseImageUrl||editingWeapon.licenseImageUrl,
                licenseImageUrlBack:editingWeapon.licenses[0]?.licenseImageUrlBack||editingWeapon.licenseImageUrlBack,
                description:editingWeapon.description,condition:editingWeapon.condition,
                forRent:editingWeapon.for_rent,rentPrice:parseFloat(editingWeapon.rent_price)||0,
                forSale:editingWeapon.for_sale,salePrice:parseFloat(editingWeapon.sale_price)||0,
              };
              let savedId:string;
              if(editingWeapon.id){
                await api.updateWeapon(editingWeapon.id,payload);
                savedId=editingWeapon.id;
                setWeapons(p=>p.map(w=>w.id===editingWeapon.id?{...w,...editingWeapon}:w));
              }else{
                const created=await api.createWeapon(payload);
                savedId=created.id;
                setWeapons(p=>[...p,{...editingWeapon,id:created.id}]);
              }
              // Save individual licenses
              for(const lic of editingWeapon.licenses){
                if(!lic.id){
                  await api.createWeaponLicense({
                    weaponId:savedId,partType:lic.partType,serialNumber:lic.serialNumber,
                    licenseNumber:lic.licenseNumber,licenseType:lic.licenseType,
                    licenseHolder:lic.licenseHolder,licenseExpires:lic.licenseExpires,
                    licenseImageUrl:lic.licenseImageUrl,licenseImageUrlBack:lic.licenseImageUrlBack,
                    caliber:lic.caliber,notes:lic.notes,
                  });
                }else{
                  await api.updateWeaponLicense(lic.id,{
                    partType:lic.partType,serialNumber:lic.serialNumber,
                    licenseNumber:lic.licenseNumber,licenseType:lic.licenseType,
                    licenseHolder:lic.licenseHolder,licenseExpires:lic.licenseExpires,
                    licenseImageUrl:lic.licenseImageUrl,licenseImageUrlBack:lic.licenseImageUrlBack,
                    caliber:lic.caliber,notes:lic.notes,
                  });
                }
              }
              setWeaponSavedId(savedId);
            }catch(err:any){alert('Fel: '+err.message);}
          }} style={{background:'#EE8F09',color:'#fff',border:'none',borderRadius:4,padding:'10px 24px',fontWeight:700,cursor:'pointer',fontSize:14}}>
            Spara vapen
          </button>
        </div>
      )}
    </div>
  </div>
)}

{/* ── License edit modal ────────────────────────────────────── */}
{editingLicense!==null&&editingLicenseIdx!==null&&(
  <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.9)',zIndex:3100,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
    <div style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:7,padding:24,width:'100%',maxWidth:560,maxHeight:'92vh',overflowY:'auto'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <h3 style={{margin:0,color:'#EE8F09',fontSize:17}}>Redigera licens</h3>
        <button type="button" onClick={()=>{setEditingLicense(null);setEditingLicenseIdx(null);setCropState(null);}} style={{background:'none',border:'none',color:T.textSub,fontSize:22,cursor:'pointer'}}>×</button>
      </div>

      {/* Part type selector */}
      <div style={{marginBottom:14}}>
        <label style={{display:'block',fontSize:11,color:T.textSub,marginBottom:6}}>Vapendel</label>
        <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
          {['Helgevär','Pipa','Låda','Slutstycke','Magasin','Övrigt'].map(pt=>(
            <button key={pt} type="button" onClick={()=>setEditingLicense(p=>p?{...p,partType:pt}:p)}
              style={{padding:'6px 14px',borderRadius:20,fontSize:12,fontWeight:600,cursor:'pointer',border:'none',
                background:editingLicense.partType===pt?'#EE8F09':'rgba(238,143,9,0.15)',
                color:editingLicense.partType===pt?'#fff':'#EE8F09'}}>
              {pt}
            </button>
          ))}
        </div>
      </div>

      {/* Photo capture for front and back with crop */}
      <div style={{background:'rgba(238,143,9,0.08)',border:'1px solid rgba(238,143,9,0.25)',borderRadius:5,padding:12,marginBottom:14}}>
        <div style={{fontSize:12,fontWeight:600,color:'#EE8F09',marginBottom:10}}>Fotografera licens</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          {/* Front */}
          <div>
            <div style={{fontSize:11,color:T.textSub,marginBottom:6,fontWeight:600}}>FRAMSIDA</div>
            <label style={{display:'block',background:'rgba(238,143,9,0.2)',border:'1px solid rgba(238,143,9,0.4)',borderRadius:4,padding:'8px 0',textAlign:'center',fontSize:12,color:'#EE8F09',cursor:'pointer',fontWeight:600}}>
              Ta foto
              <input type="file" accept="image/*" capture="environment" style={{display:'none'}} onChange={async(e)=>{
                const file=e.target.files?.[0];if(!file)return;
                const reader=new FileReader();
                reader.onload=(ev)=>{
                  setCropState({dataUrl:ev.target?.result as string,side:'front',licenseIdx:editingLicenseIdx});
                };
                reader.readAsDataURL(file);
                e.target.value='';
              }}/>
            </label>
            {editingLicense.licenseImageUrl&&(
              <img src={editingLicense.licenseImageUrl} alt="Framsida" style={{width:'100%',height:90,objectFit:'cover',borderRadius:3,border:`1px solid ${T.border}`,marginTop:6}}/>
            )}
          </div>
          {/* Back */}
          <div>
            <div style={{fontSize:11,color:T.textSub,marginBottom:6,fontWeight:600}}>BAKSIDA</div>
            <label style={{display:'block',background:'rgba(238,143,9,0.2)',border:'1px solid rgba(238,143,9,0.4)',borderRadius:4,padding:'8px 0',textAlign:'center',fontSize:12,color:'#EE8F09',cursor:'pointer',fontWeight:600}}>
              Ta foto
              <input type="file" accept="image/*" capture="environment" style={{display:'none'}} onChange={async(e)=>{
                const file=e.target.files?.[0];if(!file)return;
                const reader=new FileReader();
                reader.onload=(ev)=>{
                  setCropState({dataUrl:ev.target?.result as string,side:'back',licenseIdx:editingLicenseIdx});
                };
                reader.readAsDataURL(file);
                e.target.value='';
              }}/>
            </label>
            {editingLicense.licenseImageUrlBack&&(
              <img src={editingLicense.licenseImageUrlBack} alt="Baksida" style={{width:'100%',height:90,objectFit:'cover',borderRadius:3,border:`1px solid ${T.border}`,marginTop:6}}/>
            )}
          </div>
        </div>
        {ocrLoading&&<div style={{color:'#EE8F09',fontSize:12,marginTop:8,textAlign:'center'}}>Läser licens med AI...</div>}
        {(editingLicense.licenseImageUrl||editingLicense.licenseImageUrlBack)&&!ocrLoading&&(
          <button type="button" onClick={async()=>{
            if(!editingLicense.licenseImageUrl&&!editingLicense.licenseImageUrlBack)return;
            setOcrLoading(true);
            try{
              const result=await api.ocrWeaponLicenseDual(
                editingLicense.licenseImageUrl||editingLicense.licenseImageUrlBack,
                editingLicense.licenseImageUrl&&editingLicense.licenseImageUrlBack?editingLicense.licenseImageUrlBack:undefined,
                editingLicense.partType
              );
              if(result.success&&result.data){
                const d=result.data as any;
                setEditingLicense(p=>p?{
                  ...p,
                  serialNumber:d.serialNumber||p.serialNumber,
                  caliber:d.caliber||p.caliber,
                  licenseNumber:d.licenseNumber||p.licenseNumber,
                  licenseType:d.licenseType||p.licenseType,
                  licenseHolder:d.licenseHolder||p.licenseHolder,
                  licenseExpires:d.licenseExpires||p.licenseExpires,
                  partType:d.licensePart||p.partType,
                }:p);
              }else{alert(result.error||'OCR misslyckades');}
            }catch{alert('OCR-fel');}
            setOcrLoading(false);
          }} style={{marginTop:10,width:'100%',background:'rgba(238,143,9,0.2)',color:'#EE8F09',border:'1px solid rgba(238,143,9,0.4)',borderRadius:4,padding:'8px 0',fontSize:12,fontWeight:600,cursor:'pointer'}}>
            Tolka bilder med AI
          </button>
        )}
      </div>

      {/* License fields */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
        {([
          {label:'Serienummer',key:'serialNumber',placeholder:'ABC12345'},
          {label:'Kaliber',key:'caliber',placeholder:'.308, 12/76...'},
          {label:'Licensnummer',key:'licenseNumber',placeholder:'123456-7'},
          {label:'Licenstyp',key:'licenseType',placeholder:'Jakt, Sport...'},
          {label:'Licensinnehavare',key:'licenseHolder',placeholder:'Fullständigt namn'},
          {label:'Giltig till',key:'licenseExpires',placeholder:'ÅÅÅÅ-MM-DD'},
        ] as {label:string;key:keyof WeaponLicenseEntry;placeholder:string}[]).map(({label,key,placeholder})=>(
          <div key={key as string}>
            <label style={{display:'block',fontSize:11,color:T.textSub,marginBottom:4}}>{label}</label>
            <input value={String((editingLicense as any)[key]||'')} onChange={e=>setEditingLicense(p=>p?{...p,[key]:e.target.value}:p)}
              placeholder={placeholder}
              style={{width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:3,padding:'7px 10px',color:T.text,fontSize:13,boxSizing:'border-box'}}/>
          </div>
        ))}
      </div>
      <div style={{marginBottom:14}}>
        <label style={{display:'block',fontSize:11,color:T.textSub,marginBottom:4}}>Anteckningar</label>
        <textarea value={editingLicense.notes||''} onChange={e=>setEditingLicense(p=>p?{...p,notes:e.target.value}:p)}
          placeholder="Noteringar om denna licens..."
          style={{width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:3,padding:'7px 10px',color:T.text,fontSize:13,minHeight:60,resize:'vertical',boxSizing:'border-box'}}/>
      </div>

      <div style={{display:'flex',gap:12,justifyContent:'flex-end'}}>
        <button type="button" onClick={()=>{setEditingLicense(null);setEditingLicenseIdx(null);}} style={{background:T.surfaceHover,color:T.textSub,border:`1px solid ${T.border}`,borderRadius:4,padding:'10px 20px',cursor:'pointer'}}>Avbryt</button>
        <button type="button" onClick={()=>{
          if(editingLicenseIdx===null)return;
          setEditingWeapon(p=>{
            if(!p)return p;
            const licenses=[...p.licenses];
            licenses[editingLicenseIdx]={...editingLicense!};
            return{...p,licenses};
          });
          setEditingLicense(null);setEditingLicenseIdx(null);
        }} style={{background:'#EE8F09',color:'#fff',border:'none',borderRadius:4,padding:'10px 24px',fontWeight:700,cursor:'pointer'}}>
          OK
        </button>
      </div>
    </div>
  </div>
)}

{/* ── Crop UI ──────────────────────────────────────────────── */}
{cropState&&(()=>{
  const CROP_IMG_ID='weapon-crop-img';
  async function confirmCrop(){
    // Get displayed image dimensions
    const img=document.getElementById(CROP_IMG_ID) as HTMLImageElement;
    if(!img){return;}
    // Default: use center 85% of image as crop (good for document capture)
    const margin=0.08;
    const canvas=document.createElement('canvas');
    const srcX=Math.round(img.naturalWidth*margin);
    const srcY=Math.round(img.naturalHeight*margin);
    const srcW=Math.round(img.naturalWidth*(1-margin*2));
    const srcH=Math.round(img.naturalHeight*(1-margin*2));
    canvas.width=srcW;canvas.height=srcH;
    const ctx=canvas.getContext('2d', { colorSpace: 'srgb' })!;
    ctx.drawImage(img,srcX,srcY,srcW,srcH,0,0,srcW,srcH);
    const croppedDataUrl=canvas.toDataURL('image/jpeg',0.92);
    // Upload cropped image
    try{
      const blob=await fetch(croppedDataUrl).then(r=>r.blob());
      const file=new File([blob],'license.jpg',{type:'image/jpeg'});
      const uploadedUrl=await uploadFile(file,'images');
      if(cropState&&cropState.side==='front'){
        setEditingLicense(p=>p?{...p,licenseImageUrl:uploadedUrl}:p);
      }else{
        setEditingLicense(p=>p?{...p,licenseImageUrlBack:uploadedUrl}:p);
      }
    }catch{
      // fallback: use data URL directly
      if(cropState&&cropState.side==='front'){
        setEditingLicense(p=>p?{...p,licenseImageUrl:croppedDataUrl}:p);
      }else{
        setEditingLicense(p=>p?{...p,licenseImageUrlBack:croppedDataUrl}:p);
      }
    }
    setCropState(null);
  }
  return(
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.95)',zIndex:4000,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
      <div style={{color:'#fff',fontSize:14,marginBottom:12,textAlign:'center',padding:'0 16px'}}>
        {cropState.side==='front'?'Framsida':'Baksida'} — kontrollera att hela licensen syns, klipp sedan
      </div>
      <div style={{position:'relative',width:'calc(100vw - 32px)',maxWidth:480,maxHeight:'calc(100vh - 180px)'}}>
        <img id={CROP_IMG_ID} src={cropState.dataUrl} alt="Licens" style={{width:'100%',maxHeight:'calc(100vh - 200px)',objectFit:'contain',display:'block',borderRadius:4}}/>
        {/* Overlay dimming edges to show crop zone */}
        <div style={{position:'absolute',inset:0,border:'8% solid rgba(0,0,0,0.55)',borderRadius:4,pointerEvents:'none',boxSizing:'border-box'}}/>
        <div style={{position:'absolute',inset:'8%',border:'2px solid #EE8F09',borderRadius:3,pointerEvents:'none',boxSizing:'border-box'}}/>
        <div style={{position:'absolute',inset:'8%',display:'flex',alignItems:'center',justifyContent:'center',pointerEvents:'none'}}>
          <span style={{background:'rgba(238,143,9,0.2)',color:'#EE8F09',fontSize:11,padding:'2px 8px',borderRadius:10,fontWeight:600}}>Klipps ut</span>
        </div>
      </div>
      <div style={{display:'flex',gap:16,marginTop:16}}>
        <button type="button" onClick={()=>setCropState(null)} style={{background:'rgba(255,255,255,0.15)',color:'#fff',border:'none',borderRadius:4,padding:'12px 24px',fontSize:14,cursor:'pointer'}}>Avbryt</button>
        <button type="button" onClick={confirmCrop} style={{background:'#EE8F09',color:'#fff',border:'none',borderRadius:4,padding:'12px 28px',fontSize:14,fontWeight:700,cursor:'pointer'}}>Klipp ut</button>
      </div>
    </div>
  );
})()}

            {/* Loan form modal (blankett 551.26) */}
            {loanForm&&loanFormWeapon&&(
              <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.85)',zIndex:3500,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
                <div style={{background:'#1a2a1a',border:'1px solid rgba(255,255,255,0.15)',borderRadius:7,padding:24,width:'100%',maxWidth:580,maxHeight:'92vh',overflowY:'auto'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                    <h3 style={{margin:0,color:'#EE8F09',fontSize:17}}>Hyr ut — blankett 551.26</h3>
                    <button type="button" onClick={()=>{setLoanForm(null);setLoanFormWeapon(null);}} style={{background:'none',border:'none',color:'#ccc',fontSize:22,cursor:'pointer'}}>x</button>
                  </div>

                  <div style={{background:'rgba(238,143,9,0.1)',border:'1px solid rgba(238,143,9,0.3)',borderRadius:4,padding:10,marginBottom:16,fontSize:12,color:'#f0c060'}}>
                    <strong>Vapen:</strong> {loanFormWeapon.name} &nbsp;|&nbsp; <strong>Serienr:</strong> {loanFormWeapon.serialNumber||'—'} &nbsp;|&nbsp; <strong>Kaliber:</strong> {loanFormWeapon.caliber||'—'}<br/>
                    Fyll i låntagarens uppgifter. Klicka "Generera blankett" för förifylld polisblankett 551.26.
                  </div>

                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                    {([
                      {label:'Låntagarens namn',key:'borrowerName',placeholder:'För- och efternamn',full:true},
                      {label:'Personnummer',key:'borrowerPersonnummer',placeholder:'YYYYMMDD-XXXX',full:false},
                      {label:'E-post',key:'borrowerEmail',placeholder:'email@example.com',full:false},
                      {label:'Telefon',key:'borrowerPhone',placeholder:'08-...',full:false},
                      {label:'Mobiltelefon',key:'borrowerPhoneMobile',placeholder:'070-...',full:false},
                      {label:'Adress',key:'borrowerAddress',placeholder:'Gatuadress',full:true},
                      {label:'Postnummer',key:'borrowerPostalCode',placeholder:'123 45',full:false},
                      {label:'Ort',key:'borrowerCity',placeholder:'Stockholm',full:false},
                      {label:'Från datum',key:'loanStart',placeholder:'ÅÅÅÅ-MM-DD',full:false},
                      {label:'Till datum',key:'loanEnd',placeholder:'ÅÅÅÅ-MM-DD',full:false},
                    ] as {label:string;key:keyof LoanFormData;placeholder:string;full:boolean}[]).map(({label,key,placeholder,full})=>(
                      <div key={key as string} style={{gridColumn:full?'span 2':undefined}}>
                        <label style={{display:'block',fontSize:11,color:T.textSub,marginBottom:3}}>{label}</label>
                        <input value={String((loanForm as any)[key]||'')} onChange={e=>setLoanForm(p=>p?{...p,[key]:e.target.value}:p)}
                          placeholder={placeholder}
                          style={{width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:3,padding:'7px 10px',color:T.text,fontSize:13,boxSizing:'border-box'}}/>
                      </div>
                    ))}
                  </div>

                  <div style={{marginTop:10}}>
                    <label style={{display:'block',fontSize:11,color:T.textSub,marginBottom:3}}>Ändamål</label>
                    <select value={loanForm.purpose} onChange={e=>setLoanForm(p=>p?{...p,purpose:e.target.value}:p)}
                      style={{width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:3,padding:'7px 10px',color:T.text,fontSize:13,boxSizing:'border-box'}}>
                      <option value="Jakt">Jakt</option>
                      <option value="Målskjutning">Målskjutning</option>
                      <option value="Övrigt">Övrigt</option>
                    </select>
                  </div>

                  <div style={{marginTop:10}}>
                    <label style={{display:'block',fontSize:11,color:T.textSub,marginBottom:3}}>Anteckningar / Omständigheter</label>
                    <textarea value={loanForm.notes} onChange={e=>setLoanForm(p=>p?{...p,notes:e.target.value}:p)}
                      placeholder="Omständigheter sökanden vill åberopa..."
                      style={{width:'100%',background:T.surface,border:`1px solid ${T.border}`,borderRadius:3,padding:'7px 10px',color:T.text,fontSize:13,minHeight:64,resize:'vertical',boxSizing:'border-box'}}/>
                  </div>

                  <div style={{marginTop:20,display:'flex',gap:12,justifyContent:'flex-end',flexWrap:'wrap'}}>
                    <button type="button" onClick={()=>{setLoanForm(null);setLoanFormWeapon(null);}} style={{background:T.surfaceHover,color:T.textSub,border:`1px solid ${T.border}`,borderRadius:4,padding:'10px 18px',cursor:'pointer'}}>Avbryt</button>
                    <button type="button" onClick={async()=>{
                      setLoanLoading(true);
                      try{
                        const loan=await api.createWeaponLoan({...loanForm});
                        const url=`/api/weapons/${loanFormWeapon.id}/loan-form/${loan.id}`;
                        const accessToken=localStorage.getItem('qvenari_access_token')||'';
                        const res=await fetch(url,{headers:{...bearerHeaders(accessToken)}});
                        const html=await res.text();
                        const win=window.open('','_blank');
                        if(win){win.document.write(html);win.document.close();}
                      }catch(err:any){alert('Fel: '+err.message);}
                      setLoanLoading(false);
                    }} disabled={loanLoading} style={{background:'#EE8F09',color:'#fff',border:'none',borderRadius:4,padding:'10px 24px',fontWeight:700,cursor:'pointer',fontSize:14,opacity:loanLoading?0.6:1}}>
                      {loanLoading?'Genererar...':'Generera blankett 551.26'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* === VERIFIERA IDENTITET === */}
      <div style={{ background: T.surface, borderRadius: 10, padding: '16px 20px', marginBottom: 16, border: `1px solid ${T.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ fontSize: 18, fontWeight: 700, color: T.text }}>Verifiera identitet</span>
          {auth.getUser() && (auth.getUser() as any)?.verification_level >= 2 && (
            <span style={{ background: '#22c55e', color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>✓ Verifierad</span>
          )}
        </div>
        <VerifyIdentityPanel user={auth.getUser()} token={token||''} onVerified={(_level) => {}} T={T} />
      </div>

      {/* ── SPARA KNAPP (bottom) ── */}
      <div style={{display:'flex',alignItems:'center',gap:12,justifyContent:'flex-end',paddingBottom:40}}>
        {err&&<span style={{color:T.danger,fontSize:13}}>{err}</span>}
        {saved&&<span style={{color:T.success,fontSize:13,fontWeight:500}}>Profilen sparad!</span>}
        <button onClick={()=>setEditMode(false)} style={{...bSc,padding:'10px 20px',fontSize:13}}>Stäng</button>
        <button onClick={async()=>{await save();setEditMode(false);}} disabled={saving} style={{...bP,opacity:saving?0.6:1,minWidth:160}}>{saving?'Sparar...':'Spara & stäng'}</button>
      </div>
    </div>
  );
}


